import { number } from "framer-motion";
import { ApiClient } from "./infra/ApiClient";
import { LexApiClient } from "./infra/LexApiClient";
import { userManager } from "./oidcAuth";
import { StatusType } from "./mock-data/mockFinancialData";
import { CreateSimulatedOrder, SimulatedOrder } from "./types";
import { CommercialCycleStatus } from "./orderCycle";
import { toSupportRequestFormData, type SupportRequestPayload } from "./orderSupportRequestPayload";
import type {
  CommercialRevenueClientDetails,
  CommercialRevenuePage,
  CommercialRevenueQuery,
} from "@/types/commercialRevenue";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api";
const apiClient = new ApiClient(API_BASE_URL, "access_token");
export const apiClientIdToken = new ApiClient(API_BASE_URL, "id_token"); /* Token com email */
const lexApiClient = new LexApiClient();

// Interfaces and other API exports from the original file are preserved below.
// This is a condensed representation.
export interface Product {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  codigoProduto: string;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  relatedProductIds?: number[];
  isPrintedProduct: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Client extends CustomerData {
  id?: number;
  clientId?: number;
  codigo_cnx: string;
  nome_cliente: string;
  cnpj?: number | string;
  email?: string;
  telefone?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  clientSchoolIdLex?: string;
  created_at?: string;
  updated_at?: string;
  shippingFee?: number;
  comment?: string;
  paymentPlanId?: number;
  maxInstallments?: number | null;
  paymentConfigurationIds?: string[];
  /** Abre chamado no Zendesk quando o cliente cria um pedido (Features/NewOrderZendeskTicketFeature). */
  createZendeskTicketOnOrder?: boolean;
  // Cadastro de grupo e endereços (customerGroupAddressFeature). Só vêm com a feature ligada no backend;
  // os campos legados (clientCustomerGroup, clientAddress...) continuam valendo.
  customerGroupId?: number | null;
  customerGroup?: CustomerGroupSummary | null;
  addresses?: ClientAddress[];
  defaultBillingAddress?: ClientAddress | null;
  defaultShippingAddress?: ClientAddress | null;
}

export interface CustomerGroupSummary {
  id: number;
  code: string;
  name: string;
}

export interface CustomerGroup extends CustomerGroupSummary {
  /** Nulo nos grupos criados no backoffice. */
  magentoGroupId?: number | null;
  isActive: boolean;
  importedAt?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  /** Clientes vinculados ao grupo. */
  clientCount: number;
}

/** Criação e edição de grupo no backoffice. Nome vazio: a API usa o código. */
/** Página de uma listagem da API; total é o total com os filtros, não só os da página. */
export interface PagedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type CustomerGroupStatusFilter = "active" | "inactive" | "all";

export interface CustomerGroupListParams {
  page?: number;
  pageSize?: number;
  /** Parte do código ou do nome, ou o ID do Magento exato. */
  search?: string;
  status?: CustomerGroupStatusFilter;
}

export interface SaveCustomerGroupDto {
  magentoGroupId?: number | null;
  code: string;
  name?: string | null;
}

export interface CustomerGroupImportItem {
  line: number;
  magentoGroupId: number;
  code: string;
  name: string;
  previousCode?: string | null;
  previousName?: string | null;
}

export interface CustomerGroupImportRejectedRow {
  line: number;
  reason: string;
  raw: string;
}

export type CustomerGroupHistoryAction = "Import" | "Create" | "Update" | "Deactivate" | "Reactivate";

/** Grupo como gravado no histórico (previous/current). */
export interface CustomerGroupHistorySnapshot {
  customerGroupId?: number | null;
  magentoGroupId?: number | null;
  code: string;
  name: string;
  isActive: boolean;
}

/** Linha de customer_group_history: resumo de uma importação (sem grupo) ou alteração de um grupo. */
export interface CustomerGroupHistoryEntry {
  id: number;
  customerGroupId?: number | null;
  groupCode?: string | null;
  groupName?: string | null;
  importId?: string | null;
  action: CustomerGroupHistoryAction | string;
  source: string;
  previous?: CustomerGroupHistorySnapshot | null;
  current?: CustomerGroupHistorySnapshot | null;
  details?: {
    /** "clientGroups" na importação de grupos por cliente; ausente na importação do CSV de grupos. */
    type?: string | null;
    fileName?: string | null;
    fileSize?: number | null;
    counts?: Partial<
      Record<"totalLines" | "inserted" | "updated" | "unchanged" | "rejected" | "missingFromFile" | "linked" | "moved", number>
    >;
  } | null;
  performedBy?: string | null;
  performedAt: string;
}

/**
 * Resultado de um lote da importação do CSV de grupos (prévia com dryRun ou gravação). Os grupos fora do arquivo
 * são calculados pela tela, que tem os IDs do arquivo inteiro.
 */
export interface CustomerGroupImportResult {
  dryRun: boolean;
  counts: {
    totalLines: number;
    inserted: number;
    updated: number;
    unchanged: number;
    rejected: number;
  };
  inserted: CustomerGroupImportItem[];
  updated: CustomerGroupImportItem[];
  unchanged: CustomerGroupImportItem[];
  rejected: CustomerGroupImportRejectedRow[];
}

/** Conclusão da importação de grupos em lotes: vira o resumo no histórico (um por importId). */
export interface CompleteCustomerGroupImport {
  fileName: string;
  fileSize: number;
  totalBatches: number;
  counts: CustomerGroupImportResult["counts"];
  rejected: CustomerGroupImportRejectedRow[];
  missingFromFile: { magentoGroupId: number; code: string }[];
}

/** Cliente afetado pela importação de grupos por cliente; previous* só vêm preenchidos nos movidos. */
export interface ClientCustomerGroupImportItem {
  line: number;
  clientId: number;
  clientCodeCnx: string;
  clientCompanyName: string;
  /** Só os 14 dígitos. */
  cnpj: string;
  customerGroupId: number;
  magentoGroupId: number;
  groupCode: string;
  groupName: string;
  previousCustomerGroupId?: number | null;
  previousGroupCode?: string | null;
}

/** Lote de uma importação CSV em lotes; importId é o mesmo em todos os lotes e na conclusão de uma importação. */
export interface CsvImportBatch {
  importId: string;
  fileName: string;
  batchNumber: number;
  totalBatches: number;
  header: string;
  lines: { line: number; text: string }[];
}

export interface CompleteClientCustomerGroupImport {
  fileName: string;
  fileSize: number;
  totalBatches: number;
  counts: ClientCustomerGroupImportResult["counts"];
  /** Rejeitadas desta parte; o resumo vai em partes (part de totalParts) para o corpo não passar do limite do proxy. */
  rejected: CustomerGroupImportRejectedRow[];
  part: number;
  totalParts: number;
}

/** Resultado da importação do CSV de clientes com grupo (prévia com dryRun ou gravação). */
export interface ClientCustomerGroupImportResult {
  dryRun: boolean;
  counts: {
    totalLines: number;
    /** Clientes sem grupo que passam a ter. */
    linked: number;
    /** Clientes que trocam de grupo: o CSV sobrescreve o grupo do portal. */
    moved: number;
    unchanged: number;
    rejected: number;
  };
  linked: ClientCustomerGroupImportItem[];
  moved: ClientCustomerGroupImportItem[];
  unchanged: ClientCustomerGroupImportItem[];
  rejected: CustomerGroupImportRejectedRow[];
}

/** Billing = endereço de cobrança; Shipping = endereço de entrega. */
export type ClientAddressType = "Billing" | "Shipping";

/** Endereço no formato comum ao cliente CNPJ (ClientAddress) e ao cliente pessoa física (PhysicalClientAddress). */
export interface Address {
  id: number;
  addressType: ClientAddressType;
  postcode: string;
  street: string;
  number?: string | null;
  district?: string | null;
  complement?: string | null;
  city: string;
  state: string;
  label?: string | null;
  isDefault: boolean;
  isActive: boolean;
  source: string;
  externalId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface ClientAddress extends Address {
  clientId: number;
}

export interface PhysicalClientAddress extends Address {
  physicalClientId: number;
}

export interface UpdateClientAddressDto {
  postcode: string;
  street: string;
  number: string;
  district: string;
  complement?: string;
  city: string;
  state: string;
  label?: string;
}

export interface CreateClientAddressDto extends UpdateClientAddressDto {
  addressType: ClientAddressType;
  isDefault?: boolean;
}

export interface ChangeClientCnpjResult {
  sourceClientId: number;
  targetClientId: number;
  scheduledOrdersMoved: number;
  profilesCreated: number;
  profilesDeactivated: number;
}

export interface PaymentPlan {
  paymentPlanId: number;
  planName: string;
  description?: string;
  allowsCustomInstallments?: boolean;
}

export interface PaymentConfiguration {
  $id: string | null;
  pagamentoDynamics: string | null;
  pagamentoMagendo: number | null;
  diaPagamento: string | null;
  meses: number | null;
}

/** Fallback: planos sem a flag explícita são tratados como customizados apenas se forem o ID 3 (ver handoff de design). */
export const isCustomPaymentPlan = (plan?: PaymentPlan | null): boolean =>
  !!plan && (plan.allowsCustomInstallments ?? plan.paymentPlanId === 3);

export interface ProfileClient {
  id: number;
  client_id: number;
  codigo_produto: string;
  descricao_produto: string;
  quantidade_contratual: number;
  desconto: number;
  adjustment: number;
  created_at?: string;
  updated_at?: string;
  isScheduledProduct?: boolean;
  schedulingCategory?: ScheduledOrderRuleCategory | null;
  schedulingReason?: string | null;
  scheduledTargetProductId?: number | null;
  isOptional?: boolean;
  // Vínculo "sem frete e sem remessa impressa"; onde tem efeito vem pronto da API (shipmentExemptionApplies)
  isShipmentExempt?: boolean;
  shipmentExemptionApplies?: boolean;
  // Base de frete por unidade das remessas do produto, cobrada no pedido principal/complementar
  scheduledShipmentFreightBase?: number | null;
}

export interface Brand {
  id: number;
  name: string;
  created_at?: string;
}

export interface DeliveryType {
  id: number;
  name: string;
  number: number;
  created_at?: string;
}

export interface CustomerData {
  clientId?: number;
  clientAddress?: string;
  clientCity?: string;
  clientNumber?: string;
  clientDistrict?: string;
  clientComplement?: string;
  clientPostCode?: string;
  clientCnpj?: string;
  clientCodeCnx?: string;
  clientCompanyName?: string;
  clientCustomerGroup?: string;
  clientEmail?: string;
  clientFantasyName?: string;
  clientPhone?: string;
  clientSchoolIdLex?: string;
  clientState?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  id?: string;
  orderNumber: string;
  clientCode: string;
  clientName: string;
  totalAmount: number;
  status: string;
  createdAt?: string;
  portalB2BOrderStatus: StatusType;
  items?: OrderItem[];
  clientCnpj?: number | string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientPhone?: string;
  clientEmail?: string;
  clientCompanyName?: string;
  clientNumber?: string;
  clientDistrict?: string;
  clientPostCode?: string;
  couponCode?: string | null;
  couponDiscount?: number;
  couponDiscountAmount?: number;
  subtotalAmount?: number;
  totalItems?: number;
  updatedAt?: string;
  customer?: CustomerData;
  quantityInstallment?: number;
  userEmail?: string | null;
  deliveryDate: string;
  financialSituation: string;
  shippingAmount: number;
  previsionDelivery?: string;
  // Endereços do pedido (customerGroupAddressFeature), em GET /orders/{id}, /orders/history e /orders/client/{code}: o
  // de entrega gravado no pedido e o padrão de cobrança atual do cliente. shippingAddress vem null em pedidos antigos,
  // sem endereço de entrega vinculado: nesses, a entrega é o endereço de faturamento.
  billingAddress?: ClientAddress | null;
  shippingAddress?: ClientAddress | null;
  cycleCode?: number | null;
  isScheduled?: boolean;
  // Cancelamento (ver .claude/docs/frontend_order_cancellation.md)
  webhookScheduledAt?: string | null;
  webhookSentAt?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancellationReason?: string | null;
  cancellationTicketId?: number | null;
  cancellationTicketCreatedAt?: string | null;
  /** Quem solicitou o cancelamento que abriu o ticket (gravado junto com a referência do ticket). */
  cancellationTicketCreatedBy?: string | null;
}

export interface OrderItem {
  id?: string;
  productId?: number;
  orderId?: string;
  productCode: string;
  productDescription: string;
  unitPrice: number;
  quantityOrdered: number;
  contractualDiscount: string;
  discountAmount: number;
  itemTotal: number;
  deliveryTypeId?: number;
  brandId?: number;
  productSeries?: string;
  educationLevel?: string | null;
  brand?: string | null;
  academicYear?: number | null;
  productSku?: string;
  quantityContracted?: number;
  netPrice: number;
  createdAt?: string;
  purchaseOrderId?: string;
  useId?: number /*  1 = Aluno, 2 = Professor */;
  utilizacao?: string | null;
  /** Unidades cobertas por cupom por itens (parte de quantityOrdered; o restante é pago). */
  couponQuantity?: number;
  /** Desconto do cupom neste item (já abatido de itemTotal). */
  couponDiscountAmount?: number;
}

export type CouponDiscountType = "Percentage" | "FixedAmount" | "Items";

/** Item bonificado de um cupom por itens. */
export interface CouponItem {
  productId: number;
  productCode?: string | null;
  productSku?: string | null;
  productDescription?: string | null;
  seriesName?: string | null;
  segmentName?: string | null;
  /** Quantidade bonificada pelo cupom. */
  quantity: number;
  /** Quantidade contratual do cliente no ciclo (limite de `quantity`). */
  contractualQuantity?: number | null;
}

export interface CouponItemInput {
  productId: number;
  quantity: number;
}

export interface Coupon {
  id: string;
  name: string;
  code: string;
  discountType: CouponDiscountType;
  value: number;
  /** Ciclo comercial (ano) do cupom por itens. */
  cycleYear?: number | null;
  /** Itens bonificados (cupom por itens). */
  items?: CouponItem[];
  createdAt: string;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** Desativado: não pode mais ser aplicado em pedidos (pode ser reativado). */
  isActive: boolean;
  /** Quantidade de usos permitida (1 = uso único); null = ilimitado. */
  maxUses: number | null;
  /** Datas de Brasília (yyyy-MM-dd), inclusivas; null = sem limite. */
  validFrom?: string | null;
  validUntil?: string | null;
  validityStatus: CouponValidityStatus;
  /** Clientes elegíveis (código do cliente); vazio = qualquer cliente. */
  clientCodes: string[];
  /** Clientes elegíveis com nome fantasia (para exibição). */
  clients?: CouponClient[];
  usedCount: number;
  /** Limite de usos atingido. */
  isUsed: boolean;
  /** Reserva ativa em um checkout em andamento (bloqueio temporário, não é um status). */
  isReserved?: boolean;
  usages: CouponUsage[];
  /** Comentários de auditoria (imutáveis), do mais recente para o mais antigo. */
  comments?: CouponComment[];
}

export type CouponCommentAction = "Created" | "Updated" | "Deactivated" | "Reactivated";

export interface CouponComment {
  action: CouponCommentAction;
  comment: string;
  createdBy: string;
  createdAt: string;
}

export type CouponValidityStatus = "Scheduled" | "Active" | "Expired";

export interface CouponClient {
  code: string;
  /** Nome fantasia; vazio quando o cliente não é encontrado. */
  name: string;
}

export interface CouponUsage {
  usedBy: string;
  clientCode?: string | null;
  clientName?: string | null;
  usedAt: string;
  discountAmount: number;
  /** Sobra do desconto reservada para pedidos programados. */
  scheduledDiscountAmount?: number;
  /** Quanto da sobra já foi abatido nas cobranças dos programados. */
  scheduledAppliedAmount?: number;
  orders: CouponOrderReference[];
}

export interface CouponInput {
  name: string;
  code: string;
  discountType: CouponDiscountType;
  value: number;
  cycleYear?: number | null;
  items?: CouponItemInput[];
  /** Quantidade de usos; padrão 1 (uso único); null = ilimitado. */
  maxUses: number | null;
  validFrom: string | null;
  validUntil: string | null;
  clientCodes: string[];
  /** Comentário de auditoria obrigatório (não pode ser editado depois). */
  comment: string;
}

/** Prioridade do desconto: Annual > Semestral (imediatos) > SecondSemester > Exam (programados). */
export type CouponBucket = "Annual" | "Semestral" | "SecondSemester" | "Exam";

export interface CouponValidationItem {
  itemKey: string;
  bucket?: CouponBucket;
  productId?: number | null;
  productCode: string;
  productsSubtotal: number;
  /** Quantidade total do item no pedido (cupom por itens). */
  quantity?: number;
  /** Quantidade já bonificada (Professor) — o cupom só cobre as unidades pagas. */
  bonifiedQuantity?: number;
}

export interface CouponAllocation extends CouponValidationItem {
  discountAmount: number;
  /** Unidades cobertas pelo cupom neste item (cupom por itens). */
  couponQuantity?: number;
}

export interface CouponOrderReference {
  id: string;
  orderNumber: string;
  /** Parcela do desconto do cupom neste pedido. */
  discountAmount: number;
  /** Valor total do pedido (já com desconto e frete); só vem na listagem. */
  totalAmount?: number | null;
}

export interface CouponReservation {
  reservationId: string;
  expiresAt: string;
  coupon: CouponValidation;
}

export interface CouponValidation {
  id: string;
  name: string;
  code: string;
  discountType: CouponDiscountType;
  value: number;
  discountAmount: number;
  /** Parte do desconto abatida agora (pedido imediato). */
  immediateDiscountAmount?: number;
  /** Sobra aplicada no 2º semestre programado (abatida na cobrança do programado). */
  scheduledSecondSemesterDiscountAmount?: number;
  /** Sobra aplicada em avaliação/simulado programado. */
  scheduledExamDiscountAmount?: number;
  productsSubtotal: number;
  productsTotalAfterDiscount: number;
  allocations: CouponAllocation[];
}

// Cupons usam sempre o ID token (carrega a role de administrador e o e-mail): a reserva, o create-order
// (com cupom) e a confirmação precisam identificar o mesmo usuário.
export const couponApi = {
  getAll: () => apiClientIdToken.get<{ data: Coupon[] }>("/coupons"),
  /** Exporta a listagem em .xlsx (mesma rota da listagem, com o parâmetro export). */
  exportXlsx: () =>
    apiClientIdToken.get<Blob>("/coupons?export=true", { responseType: "blob" }),
  create: (coupon: CouponInput) => apiClientIdToken.post<Coupon>("/coupons", coupon),
  update: (id: string, coupon: CouponInput) => apiClientIdToken.put<Coupon>(`/coupons/${id}`, coupon),
  delete: (id: string) => apiClientIdToken.delete<void>(`/coupons/${id}`),
  setActive: (id: string, isActive: boolean, comment: string) =>
    apiClientIdToken.patch<Coupon>(`/coupons/${id}/active`, { isActive, comment }),
  validate: (code: string, items: CouponValidationItem[], clientCode?: string | null, cycleYear?: number | null) =>
    apiClientIdToken.post<CouponValidation>("/coupons/validate", { code, clientCode, cycleYear, items }),
  reserve: (code: string, items: CouponValidationItem[], clientCode?: string | null, cycleYear?: number | null) =>
    apiClientIdToken.post<CouponReservation>("/coupons/reserve", { code, clientCode, cycleYear, items }),
  commit: (reservationId: string, orders: CouponOrderReference[]) =>
    apiClientIdToken.post<Coupon>(`/coupons/reservations/${reservationId}/commit`, { orders }),
  /** Libera a reserva (desfaz pedidos parciais) pela rota de commit com release=true. */
  release: (reservationId: string) =>
    apiClientIdToken.post<void>(`/coupons/reservations/${reservationId}/commit`, { orders: [], release: true }),
};

export type IntegratedOrderStatusFilter = "all" | "review" | "valid" | "routed" | "not_routed";

export type IntegratedOrderAction = "bonus_counted" | "scheduled_created" | "scheduled_updated" | "integrated_only";

/** Colunas aceitas na ordenação da listagem (ordenada no backend, por causa da paginação). */
export type IntegratedOrderSortKey =
  | "createdAt" | "client" | "product" | "cycleYear" | "quantity" | "integrationType" | "preOrder" | "situation" | "action";

/** Item de purchase_orders_b2b_request (pré-pedido do ERP) exibido na tela "Pedidos integrados". */
export interface IntegratedOrder {
  id: number;
  clientId?: number | null;
  clientCode?: string | null;
  clientName?: string | null;
  cnpjCpf?: string | null;
  productId?: number | null;
  productCode?: string | null;
  productDescription?: string | null;
  /** SKU como recebido do ERP. */
  itemId?: string | null;
  description?: string | null;
  cycleYear?: number | null;
  quantity?: number | null;
  integrationType?: number | null;
  preOrderId?: string | null;
  preOrderReference?: string | null;
  needsReview: boolean;
  reviewReason?: string | null;
  /** second_semester ou exam quando o item foi incluído em um pedido programado. */
  routedCategory?: string | null;
  /** O que a integração fez com o registro (nulo em registros em revisão e nos anteriores a este campo). */
  integrationAction?: IntegratedOrderAction | null;
  scheduledOrderId?: string | null;
  scheduledOrderNumber?: string | null;
  rawPayload?: string | null;
  createdAt: string;
  /** Última correção ou reintegração feita por esta tela. */
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  /** Comentário informado na última correção (diferente de reviewReason, o motivo da revisão). */
  reviewComment?: string | null;
  /** Somente registros em revisão podem ser editados e reintegrados. */
  canEdit: boolean;
}

export interface IntegratedOrderPage {
  items: IntegratedOrder[];
  total: number;
  page: number;
  pageSize: number;
  summary: { total: number; needsReview: number; valid: number; routed: number };
}

export interface IntegratedOrderQuery {
  search?: string;
  status?: IntegratedOrderStatusFilter;
  cycleYear?: number | null;
  integrationType?: number | null;
  sortBy?: IntegratedOrderSortKey;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface IntegratedOrderUpdate {
  cnpjCpf?: string;
  productId?: number;
  quantity?: number;
  /** Obrigatório: por que o registro está sendo corrigido. */
  comment: string;
}

export interface IntegratedOrderReintegrationResult {
  registered: boolean;
  alreadyRegistered: boolean;
  needsReview: boolean;
  bonusUpdated: boolean;
  requestId: number;
  clientCode: string;
  routedCategory?: string | null;
  integrationAction?: IntegratedOrderAction | null;
  /** True quando o item chegou com integrationType 0: recebido (200), mas nada foi gravado. */
  ignored?: boolean;
  reviewReason?: string | null;
  message: string;
}

const integratedOrderParams = (query: IntegratedOrderQuery) => {
  const params = new URLSearchParams();
  if (query.search?.trim()) params.set("search", query.search.trim());
  if (query.status && query.status !== "all") params.set("status", query.status);
  if (query.cycleYear != null) params.set("cycleYear", String(query.cycleYear));
  if (query.integrationType != null) params.set("integrationType", String(query.integrationType));
  if (query.sortBy) {
    params.set("sortBy", query.sortBy);
    params.set("sortDir", query.sortDir ?? "desc");
  }
  if (query.page) params.set("page", String(query.page));
  if (query.pageSize) params.set("pageSize", String(query.pageSize));
  return params;
};

// Tela "Pedidos integrados": usa o ID token (carrega a role de administrador e o e-mail de quem corrige/reintegra).
export const b2bRequestsApi = {
  list: (query: IntegratedOrderQuery) => {
    const params = integratedOrderParams(query).toString();
    return apiClientIdToken.get<IntegratedOrderPage>(`/b2b-requests${params ? `?${params}` : ""}`);
  },
  /** Exporta em .xlsx todos os registros do filtro (mesma rota da listagem, com o parâmetro export). */
  exportXlsx: (query: IntegratedOrderQuery) => {
    const params = integratedOrderParams({ ...query, page: undefined, pageSize: undefined });
    params.set("export", "true");
    return apiClientIdToken.get<Blob>(`/b2b-requests?${params.toString()}`, { responseType: "blob" });
  },
  /** Corrige escola (CNPJ/CPF), produto e/ou quantidade de um registro em revisão. */
  update: (id: number, changes: IntegratedOrderUpdate) =>
    apiClientIdToken.put<IntegratedOrder>(`/b2b-requests/${id}`, changes),
  /** Reenvia o registro em revisão pelo mesmo processo do ERP (escola e produto são resolvidos de novo). */
  reintegrate: (id: number, comment?: string) =>
    apiClientIdToken.post<IntegratedOrderReintegrationResult>(
      `/b2b-requests/${id}/reintegrate`,
      comment?.trim() ? { comment: comment.trim() } : {},
    ),
};

// Tela "Retornos do ERP" (invoiceReturnsFeature): notas fiscais e status de OV que o ERP enviou ao portal
// (POST /api/invoices e POST /api/invoices/sales-order-status). Rota de administrador, por isso o ID token.
export interface InvoiceReturn {
  /** Chave única na lista: "e-{id}" (gravada com sucesso) ou "m-{id}" (com pendência, guardada para tratamento). */
  key: string;
  id: number;
  receivedAt: string;
  orderNumber?: string | null;
  clientName?: string | null;
  clientCode?: string | null;
  /** Número CNX- devolvido pelo ERP no aceite do pré-pedido. */
  preOrderId?: string | null;
  /** Número de integração do pedido (14 dígitos), como texto. */
  preOrderReference?: string | null;
  kind: "invoice" | "status";
  /** false = chegou, mas ficou com pendência (não gravada por completo; o ERP recebeu 200 e a chamada ficou guardada). */
  success: boolean;
  invoiceNumber?: string | null;
  salesIds: string[];
  /** Tipos de arquivo gravados no S3 (xml, pdf). */
  files: string[];
  /** Tipos de purchase_orders_status gravados (ex.: FaturadoERPDynamics). */
  statuses: string[];
}

export interface InvoiceReturnFile {
  type?: string | null;
  fileName?: string | null;
  sizeBytes: number;
}

export interface InvoiceReturnOrder {
  salesId?: string | null;
  salesStatus?: string | null;
  fiscalDocumentNumber?: string | null;
  fiscalDocumentStatus?: string | null;
}

/** Chamada pendente em detalhe: situação, tentativas e o corpo guardado em campos editáveis (os arquivos são só informativos). */
export interface InvoiceReturnDetail {
  id: number;
  kind: "invoice" | "status";
  status: "pending" | "resolved" | "discarded";
  receivedAt: string;
  reason: string;
  message?: string | null;
  orderNumber?: string | null;
  attempts: number;
  lastAttemptAt?: string | null;
  lastAttemptBy?: string | null;
  editedAt?: string | null;
  editedBy?: string | null;
  /** false = corpo ilegível (veja rawPayload) ou chamada já baixada. */
  payloadReadable: boolean;
  preOrderReference?: string | null;
  preOrderId?: string | null;
  salesId?: string | null;
  invoiceId?: string | null;
  /** Número da nota lido do XML do arquivo guardado (só informativo: invoiceId, quando preenchido, tem prioridade). */
  derivedInvoiceId?: string | null;
  accessKey?: string | null;
  files?: InvoiceReturnFile[] | null;
  /** Status da OV enviado pelo ERP (nome do tipo, ex.: FaturadoERPDynamics). */
  erpStatus?: string | null;
  preOrderStatus?: string | null;
  orders?: InvoiceReturnOrder[] | null;
  rawPayload?: string | null;
}

/** Campo ausente = não muda; texto vazio = limpa. payloadJson substitui o corpo inteiro (corpo ilegível). */
export interface InvoiceReturnUpdate {
  preOrderReference?: string;
  preOrderId?: string;
  salesId?: string;
  invoiceId?: string;
  accessKey?: string;
  /** Status enviado pelo ERP (nome do tipo). */
  status?: string;
  preOrderStatus?: string;
  orders?: InvoiceReturnOrder[];
  payloadJson?: string;
}

export interface InvoiceReprocessResult {
  /** true = a chamada foi gravada e a pendência baixada. */
  resolved: boolean;
  needsReview: boolean;
  reason?: string | null;
  message?: string | null;
  orderNumber?: string | null;
  attempts: number;
}

export const invoiceReturnsApi = {
  /** Últimas chamadas do ERP, da mais recente para a mais antiga. */
  list: (limit = 500) => apiClientIdToken.get<InvoiceReturn[]>(`/invoices/returns?limit=${limit}`),
  /** Detalhe de uma chamada pendente (id da linha "m-{id}"). */
  getDetail: (id: number) => apiClientIdToken.get<InvoiceReturnDetail>(`/invoices/returns/${id}`),
  /** Corrige campos do corpo guardado; não reprocessa. */
  update: (id: number, changes: InvoiceReturnUpdate) =>
    apiClientIdToken.put<InvoiceReturnDetail>(`/invoices/returns/${id}`, changes),
  /** Reaplica o corpo guardado pelo mesmo serviço do ERP (idempotente). */
  reprocess: (id: number) =>
    apiClientIdToken.post<InvoiceReprocessResult>(`/invoices/returns/${id}/reprocess`, {}),
  /** Descarta a chamada pendente (apaga o corpo guardado). */
  discard: (id: number) => apiClientIdToken.post<void>(`/invoices/returns/${id}/discard`, {}),
};

export interface ProfileClientAddProductDto {
  clientId: number;
  productId: number;
  productStatus: boolean;
  contractualQuantity: number;
  discount: number;
  content: string;
  productPrice: number;
  priceReferenceYear?: number;
  // Produto opcional; ausente = obrigatório no vínculo novo e mantém o valor de vínculo existente
  isOptional?: boolean;
  // Sem frete e sem remessa impressa; ausente = false no vínculo novo e mantém o valor de vínculo existente
  isShipmentExempt?: boolean;
}

export interface ProfileHistory {
  productId: number;
  clientId: number;
  productDescription: string;
  updatedAt: string;
  createdAt: string;
  contractualQuantity: number;
  discount: number;
  isActive: boolean;
  seriesName: string | null;
  productCode: string;
  materialUseName: string | null;
  comment: {
    commentId: number;
    profileClientId: number;
    author: string;
    content: string;
    details: string;
    createdAt: string;
    isActive: number;
  } | null;
}

export interface Summary {
  totalClients: number;
  totalStudents: number;
  acv: number;
}

/* School interface from backend API */
export interface School {
  id: string;
  name: string;
  clientId: number;
  clientAddress: string;
  clientCity: string;
  clientNumber: string;
  clientDistrict: string;
  clientComplement: string;
  clientPostCode: string;
  clientCnpj: string;
  clientCodeCnx: string;
  clientCompanyName: string;
  clientCustomerGroup: string;
  clientEmail: string;
  clientFantasyName: string;
  clientPhone: string;
  clientSchoolIdLex: string;
  clientState: string;
  shippingFee: number;
  createdAt: string;
  updatedAt: string;
  quantityOfStudents: number;
  totalPurchaseOrderValue: number | null;
  isInadimplente?: boolean;
  /** Grupo de customer_group (só com customerGroupAddressFeature ligada no backend). */
  customerGroupId?: number | null;
  customerGroup?: CustomerGroupSummary | null;
}

export interface OrderDraft {
  id: number;
  clientCode: string;
  quantities: Record<number, number>;
  lastModifiedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SaveOrderDraftRequest {
  clientCode: string;
  quantities: Record<number, number>;
}

export interface ResendWebhook {
  success: boolean;
  message: string;
  timeout?: boolean;
  warning?: string;
}

// Andamento da integração direta do pedido com o ERP (erpDirectIntegrationFeature), em GET /orders/{id}/erp-integration.
export interface ErpIntegrationEvent {
  id: number;
  attempt: number;
  stepCode: string;
  stepName: string;
  /** ok, error ou info. */
  status: string;
  occurredAt: string;
  durationMs?: number | null;
  requestJson?: string | null;
  responseJson?: string | null;
  errorMessage?: string | null;
}

export interface ErpReturnStatus {
  status: string;
  salesId?: string | null;
  at?: string | null;
}

export interface ErpIntegrationSettingsValues {
  /** Pedidos ainda não enviados vão direto ao ERP, sem lambda nem Magento (padrão: false). */
  directSend: boolean;
  /** Modo sombra: o fluxo antigo também grava o payload do ERP, sem enviar (padrão: false). */
  shadow: boolean;
}

export interface ErpIntegrationDetails {
  orderId: string;
  orderNumber: string;
  /** Modo sombra: o payload é gerado e gravado, mas nada é enviado ao ERP. */
  shadowOnly: boolean;
  /** sending, sent, rejected, failed, gave_up ou shadow; nulo quando ainda não houve tentativa. */
  status?: string | null;
  integrationNumber?: number | null;
  erpOrderNumber?: string | null;
  attempts: number;
  webhookScheduledAt?: string | null;
  webhookSentAt?: string | null;
  sentAt?: string | null;
  nextAttemptAt?: string | null;
  lastError?: string | null;
  returnWaitMinutes: number;
  noReturnAfterWait: boolean;
  events: ErpIntegrationEvent[];
  returns: ErpReturnStatus[];
}

/** Ver .claude/docs/frontend_order_cancellation.md, seção 3.2. */
export interface CancellationTicket {
  zendeskTicketId?: number;
  createdAt?: string;
  subject?: string | null;
  status?: string | null;
  /** Motivo enviado na solicitação, lido da descrição do ticket no Zendesk — nulo se a consulta falhar. */
  reason?: string | null;
  /** Presente no formato "sem ticket encontrado". */
  message?: string;
}

// "Ajuda com o meu pedido": solicitações por pedido (cancelamento) e por item (troca, checklist...).
// Contrato em b2b-order-portal-api, Endpoints/OrderSupportRequestEndpoints.cs.

/** Tipo ativo de solicitação. `title` é o assunto do ticket no Zendesk — exibir sempre ele, nunca texto fixo. */
export interface OrderRequestType {
  code: string;
  title: string;
  /** `true` = solicitação sempre sobre um item do pedido (POST /requests); `false` = pedido inteiro (cancelamento). */
  requiresItem: boolean;
  /** `true` = conferência: o portal pede a quantidade recebida de cada item (ex.: divergência) e a descrição é opcional. */
  capturesReceivedQuantity?: boolean;
}

export interface OrderSupportRequest {
  id: number;
  requestType: string;
  title: string;
  zendeskTicketId: number;
  /** Status ao vivo do Zendesk (new, open, pending, hold, solved, closed); `null` quando o Zendesk não respondeu. */
  ticketStatus: string | null;
  description: string | null;
  /** Itens da solicitação (troca, divergência...), todos no mesmo ticket. `null` no cancelamento (pedido inteiro). */
  items: OrderSupportRequestItem[] | null;
  createdAt: string | null;
  createdBy: string | null;
}

export interface OrderSupportRequestItem {
  purchaseOrderItemId: string;
  productCode: string | null;
  productSku: string | null;
  productDescription: string | null;
  /** Troca: quantidade informada; `null` quando não informada. */
  quantity: number | null;
  /** Quantidade pedida do item no momento da solicitação. */
  orderedQuantity: number | null;
  /** Divergência (conferência): quantidade recebida; diferença = recebida − pedida. `null` nos demais tipos. */
  receivedQuantity: number | null;
}

/** Enviado como multipart/form-data, com as chaves dos anexos já enviados ao S3 (ver orderSupportRequestPayload.ts). */
export type CreateOrderSupportRequestDto = SupportRequestPayload;

/** `POST /orders/{id}/requests/attachments/upload-url`: pedido de URL para enviar um anexo direto ao S3. */
export interface CreateSupportAttachmentUploadDto {
  /** O mesmo `clientRequestId` da criação: a chave do arquivo fica presa a ele. */
  clientRequestId: string;
  fileName: string;
  size: number;
  /** SHA-256 (hex) do arquivo; o back confere ao ler do S3. */
  sha256: string;
}

/** URL assinada (5 min): POST de formulário com todos os `fields` e o arquivo por último, no campo "file". */
export interface SupportAttachmentUpload {
  /** Vai em `attachmentKeys` na criação da solicitação. */
  key: string;
  url: string;
  fields: Record<string, string>;
  expiresAt: string;
}

/** `GET /orders/{id}/requests/{ticketId}/comments`. */
export interface OrderSupportRequestConversation {
  /** Arquivos que o cliente enviou ao abrir a solicitação. */
  requestAttachments: OrderSupportRequestCommentAttachment[];
  /** Respostas públicas, da mais recente para a mais antiga. */
  comments: OrderSupportRequestComment[];
}

/** Resposta pública do chamado no Zendesk (nota interna nunca vem). */
export interface OrderSupportRequestComment {
  id: number;
  /** "Atendimento" para a equipe; o nome de quem escreveu quando é o cliente. */
  authorName: string;
  isFromSupport: boolean;
  /** Texto puro: exibir como texto, nunca como HTML. */
  body: string;
  createdAt: string | null;
  attachments: OrderSupportRequestCommentAttachment[];
}

export interface OrderSupportRequestCommentAttachment {
  id: number;
  fileName: string;
  contentType: string | null;
  size: number | null;
}

export interface TotalOrdersAmount {
  clientName: string;
  totalItems: number;
  itemTotal: number;
  purchaseOrderId?: string;
  orderNumber?: string;
}

export interface CreateOrderDto {
  order_number: string;
  backofficeReason: string | null;
  created_by: string;
  client_name: string;
  client_code: string;
  total_amount: number;
  subtotal_amount: number;
  coupon_code?: string | null;
  coupon_discount?: number | null;
  coupon_discount_amount?: number | null;
  /** Reserva de cupom: o backend calcula o desconto (total_amount segue bruto). */
  coupon_reservation_id?: string | null;
  coupon_items?: Array<{ productId?: number | null; productCode: string; itemTotal: number; quantityOrdered: number }>;
  total_items: number;
  status: string;
  client_company_name?: string | null;
  client_cnpj?: number | string | null;
  client_customer_group?: string | null;
  client_address?: string | null;
  client_state?: string | null;
  client_city?: string | null;
  client_phone?: string | null;
  client_email?: string | null;
  deliveryTypeId?: number;
  shipping_amount?: number;
  isScheduled?: boolean;
  cycle_code?: string;
  quantityInstallment?: number;
  paymentConfiguration?: string | null;
  sourcePurchaseOrderId?: string;
  /** Endereço de entrega (client_address Shipping) escolhido; sem ele a API usa o padrão de entrega da escola. */
  shipping_address_id?: number | null;
  items: OrderItem[];
  customer: {
    clientCodeCnx: string;
    clientCompanyName: string;
  };
}

type QuantityOrdersType = {
  quantity: number;
  status: string;
  amount: number;
  date?: string;
};
interface QuantityOrders {
  totalOrders: QuantityOrdersType;
  outstandingInstallments: QuantityOrdersType;
  overdueInstallments: QuantityOrdersType;
  nextDueDate: QuantityOrdersType;
}

export interface ParcelaLine {
  orderId: string;
  orderNumber: string;
  salesOrderId: string;
  magentoOrderId: string;
  invoiceId: string;
  installments: string;
  oldAmount: number;
  newAmount: number;
  paymentStatus: number;
  transactionType: number;
  isIsencao: boolean;
  isDevolucao: boolean;
}

export interface Parcelas {
  cnpjCpf: string;
  monthYear: string;
  dueDate: string;
  oldAmount: number;
  newAmount: number;
  paymentStatus: number;
  statusLabel: string;
  hasCorrection: boolean;
  hasBoleto: boolean;
  parmJournalTransRecId: number;
  /** RecId do boleto no Dynamics. `null` quando a transação não tem boleto. */
  recIdBoleto?: number | null;
  parmBltCodBarras: string;
  linhaDigitavel: string;
  parmBltNumeroDocumento: string;
  parmBltDataEmissao: string;
  parmBltDataVencimento: string;
  parmBltValorTitulo: number;
  parmBltAceite: string;
  parmBltAlunoNome: string;
  parmBltAtualizouHtml: number;
  parmBltBankCode: string;
  parmBltCedenteCnpjCpf: string;
  parmBltCedenteCodigo: string;
  parmBltCedenteNome: string;
  parmBltCodigoDv: string;
  parmBltCodigoOcorrencia: string;
  parmBltCodigoTransmissao: string;
  parmBltContaBancariaAgencia: string;
  parmBltContaBancariaCarteiraPadrao: string;
  parmBltContaBancariaConta: string;
  parmBltContaBancariaDigitoAgencia: string;
  parmBltContaBancariaDigitoConta: string;
  parmBltContaBancariaMensagemFixaTopoBoleto: string;
  parmBltContaBancariaOperacaoConta: string;
  parmBltContaBancariaTipoCarteiraPadrao: number;
  parmBltContaBancariaTipoDocumento: number;
  parmBltContaBancariaTipoFormaCadastramento: number;
  parmBltContaBancariaTipoImpressaoBoleto: number;
  parmBltContaBancariaVariacaoCarteiraPadrao: string;
  parmBltDescricaoOcorrencia: string;
  parmBltDetalheDaFatura: string;
  parmBltEnderecoLogradouroBairro: string;
  parmBltEnderecoLogradouroCep: string;
  parmBltEnderecoLogradouroCidade: string;
  parmBltEnderecoLogradouroComplemento: string;
  parmBltEnderecoLogradouroEndereco: string;
  parmBltEnderecoLogradouroNumero: string;
  parmBltEnderecoLogradouroUf: string;
  parmBltEnderecoSacadoBairro: string;
  parmBltEnderecoSacadoCep: string;
  parmBltEnderecoSacadoCidade: string;
  parmBltEnderecoSacadoComplemento: string;
  parmBltEnderecoSacadoEndereco: string;
  parmBltEnderecoSacadoNumero: string;
  parmBltEnderecoSacadoUf: string;
  parmBltEspecieDocumento: number;
  parmBltGmPacoteDeCursoDescricao: string;
  parmBltGmTurnoNome: string;
  parmBltGrupoDemonstrativo: string;
  parmBltMensagemInformacaoAdicional: string;
  parmBltMensagemInstrucoesCaixa: string;
  parmBltMsgReferenciaPedido: string;
  parmBltNumeroControleParticipante: string;
  parmBltSacadoCnpjCpf: string;
  parmBltSacadoNome: string;
  parmBase64PdfBoleto?: string;
  lines: ParcelaLine[];
}

export interface Boletos {
  parcelas: Parcelas[];
  resumo: {
    saldoDevedor: number;
    titulosVencidos: number;
    totalAVencer: number;
  };
}

export interface BoletoData {
  journalTransRecId: number;
  numeroDocumento: string;
  codigoBarras: string;
  bankName: string;
  linhaDigitavel: string;
  dataEmissao: string;
  dataVencimento: string;
  valorTitulo: number;
  valorOriginal: number;
  valorAtualizado: number;
  sacadoNome: string;
  sacadoCnpjCpf: string;
  sacadoEndereco: string;
  sacadoNumero: string;
  sacadoBairro: string;
  sacadoCidade: string;
  sacadoUf: string;
  sacadoCep: string;
  cedenteNome: string;
  cedenteCnpjCpf: string;
  bankCode: string;
}

export type OrderStatusType = {
  purchaseOrdersStatusTypeName: string;
  externalApiStatus: string;
  externalApiDescription: string;
  portalB2bOrderStatus: StatusType;
  dynamicsSalesId: string;
  createdAt: string;
};

export interface OrderStatus {
  lastStatus: OrderStatusType;
  statusHistory: Array<OrderStatusType>;
}

export interface ScheduledOrder {
  id: string;
  clientId: string;
  orderNumber: string;
  clientCode: string;
  clientName: string;
  scheduledDate: string | null;
  status:
  | "pending"
  | "pending_confirmation"
  | "processed"
  | "confirmed"
  | "failed"
  | "processing"
  | "completed";
  retryCount: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
  createdBy: string;
  orderPayload?: any;
  cycleCode?: string;
  deadline?: string;
  shipmentBlock: number;
  awaitingComposition?: boolean;
  cutoffAt?: string;
  frozenAt?: string;
  frozenBy?: string;
  frozenHash?: string;
  processedPurchaseOrderId?: string;
  reconstructionStatus?: "NATIVE" | "COMPLETE" | "PARTIAL" | "INSUFFICIENT_EVIDENCE";
  isFrozen?: boolean;
  couponCode?: string | null;
  couponDiscountAmount?: number | null;
  couponDiscountApplied?: boolean | null;
  /** "Items" = cupom por itens (itens bonificados em linha separada); demais = por valor. */
  couponDiscountType?: string | null;
  /** Um item por cupom: o pedido programado acumula pedidos de cupons diferentes. */
  coupons?: Array<{ code: string; discountType: string; amount: number }>;
}

export interface ScheduledOrderItemTrace {
  id: string;
  productId?: number;
  productCode: string;
  productDescription: string;
  productSeries?: string;
  segmentName?: string;
  brandName?: string;
  productSchoolYear?: number;
  productSku?: string;
  quantityOrdered: number;
  bonifiedQuantity: number;
  /** Unidades cobertas por cupom por itens (parte de quantityOrdered). */
  couponQuantity?: number;
  /** Desconto do cupom neste item (já abatido de itemTotal). */
  couponDiscountAmount?: number;
  /** Cupom (por itens) que cobre as unidades de couponQuantity. */
  couponCode?: string | null;
  unitPrice: number;
  contractualDiscount: number;
  discountAmount: number;
  netPrice: number;
  itemTotal: number;
  parentOrderNumber?: string;
  parentOrderId?: string;
  parentCreatedBy?: string;
  parentCreatedAt?: string;
}

export interface ScheduledOrderItemChange {
  id: string;
  scheduledOrderId: string;
  scheduledOrderItemId?: string;
  productCode?: string;
  productSku?: string;
  productDescription?: string;
  sourcePurchaseOrderId?: string;
  sourceOrderNumber?: string;
  changeType:
  | "ITEM_ADDED"
  | "QUANTITY_INCREASED"
  | "QUANTITY_DECREASED"
  | "ITEM_REMOVED"
  | "CONFIRMED"
  | "ORDER_FROZEN"
  | "ORDER_PROCESSING_STARTED"
  | "ORDER_PROCESSED"
  | "ORDER_PROCESSING_FAILED";
  previousQuantity?: number;
  newQuantity?: number;
  reason?: string;
  metadata: string;
  changedAt: string;
  changedBy?: string;
}

export interface ScheduledOrderCutoffPolicy {
  id: string;
  commercialCycleId: number;
  cycleYear: number;
  orderType: string;
  cutoffAt: string;
  timeZone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

type InvoiceListTypes = {
  invoiceStatusTypeName: string;
  externalApiStatus: string;
  externalApiDescription: string;
  portalB2bInvoiceStatus: string;
  createdAt: string;
};

export type invoiceList = {
  invoiceNumber: string;
  boleto?: Parcelas[];
  lastStatus: InvoiceListTypes;
  statusHistory: InvoiceListTypes[];
};
export interface InvoiceStatus {
  orderNumber: string;
  invoiceList: invoiceList[];
}

export interface ExamsSkuComposition {
  id: number;
  shipmentBlock: number;
  digitalSku: number;
  printedSku: number;
  erpDescription: string;
  seriesId: number;
  /** Nome da série (series.series_name). */
  seriesName?: string | null;
  /** Segmento do produto (catálogo). */
  segmentName?: string | null;
  orderDeadline: string;
  /** Ciclo da composição (null = vale para todos os ciclos). */
  cycleId?: number | null;
  cycleYear?: number | null;
}

export type ProductPricingItem = {
  profileClientId: number;
  productId: number;
  productCode: string;
  productDescription: string;
  materialUseName: string;
  contractualQuantity: number;
  adjustment: number;
  baseNetPrice: number;
  adjustedPrice: number;
  isActive: boolean;
  productSku: string;
  segmentName: string;
  seriesName: string;
};

export interface PricingSeries {
  seriesName: string;
  products: ProductPricingItem[];
}

export interface ProductPricingBySegment {
  segmentName: string;
  series: PricingSeries[];
}

export const administrativeApi = {
  register: () => apiClientIdToken.get<void>(`/admin/register`),
};

export const examsComposition = {
  examsSkuComposition: () =>
    apiClient.get<{ data: ExamsSkuComposition[] }>(`/exams-sku-composition`),
};

/** Resultado de POST /scheduled-orders/process?mode=redistribute ("Redistribuir entregas"). */
export interface ExamShipmentRedistributionResult {
  cycleYear: number;
  ordersAnalyzed: number;
  ordersChanged: number;
  shipmentOrders: number;
  extraOrders: number;
  distributedItems: number;
  pendingItems: number;
  /** Pedidos redistribuídos (houve mudança). */
  changedOrders: ExamShipmentRedistributedOrder[];
  errors: string[];
}

export interface ExamShipmentRedistributedOrder {
  scheduledOrderId: string;
  orderNumber: string;
  clientCode: string;
  clientName: string;
  shipmentOrders: number;
  extraOrders: number;
  distributedItems: number;
  /** Remessas (bloco + prazo "AAAA-MM-DD") criadas/atualizadas para o pedido, em ordem de prazo. */
  shipments: { shipmentBlock: number; deadline: string }[];
}

/** Pedido elegível a ser incluído na redistribuição mesmo com a remessa vencida. */
export interface ExamShipmentEligibleOrder {
  scheduledOrderId: string;
  orderNumber: string;
  clientCode: string;
  clientName: string;
  createdAt: string;
  items: Array<{
    productDescription: string;
    productSku?: string | null;
    productSeries?: string | null;
    quantity: number;
    expiredDeadlines: string[];
  }>;
}

export const scheduledOrdersApi = {
  getAll: () => apiClient.get<{ data: ScheduledOrder[] }>(
    `/scheduled-orders/all`,
  ),
  changeConfirm: (id: string, order: any) =>
    apiClient.put(`/scheduled-orders/${id}/confirm`, order),
  getById: (id: string) =>
    apiClient.get<ScheduledOrder>(`/scheduled-orders/${id}`),
  getItems: (id: string) =>
    apiClient.get<{ data: ScheduledOrderItemTrace[] }>(`/scheduled-orders/${id}/items`),
  getItemChanges: (id: string) =>
    apiClient.get<{ data: ScheduledOrderItemChange[] }>(`/scheduled-orders/${id}/item-changes`),
  getClient: (clientCode: string) => apiClient.get<{ data: ScheduledOrder[] }>(
    `/scheduled-orders/all?clientCode=${encodeURIComponent(clientCode)}`,
  ),
  sendOrders: (batchSize: number) =>
    apiClient.post<void>(`/scheduled-orders/process?batchSize=${batchSize}`),
  /** Redistribuir entregas (somente administrador): mesma rota de processamento, com mode=redistribute. */
  redistributeExamShipments: (
    cycleYear: number,
    options?: { scheduledOrderId: string; includeExpired: boolean },
  ) =>
    apiClientIdToken.post<{ data: ExamShipmentRedistributionResult }>(
      `/scheduled-orders/process?mode=redistribute&cycleYear=${cycleYear}`
      + (options ? `&scheduledOrderId=${encodeURIComponent(options.scheduledOrderId)}&includeExpired=${options.includeExpired}` : ""),
    ),
  /** Pedidos que podem ser incluídos individualmente na redistribuição mesmo com a remessa vencida. */
  getRedistributionEligible: (cycleYear: number) =>
    apiClientIdToken.get<{ data: ExamShipmentEligibleOrder[] }>(
      `/scheduled-orders/all?mode=redistribute-eligible&cycleYear=${cycleYear}`,
    ),
  sendIndividual: (orderId: string) =>
    apiClient.post<void>(`/scheduled-orders/${orderId}/process`),
};

export type ScheduledOrderRuleCategory =
  | "Immediate"
  | "ImmediateAndSecondSemester"
  | "SecondSemesterOnly"
  | "ExamScheduled"
  | "ImmediateAndExam"
  | "Unresolved";

export interface ScheduledOrderRuleShipment {
  shipmentBlock: number;
  printedSku: number;
  orderDeadline: string;
}

export interface ScheduledOrderRuleProduct {
  cycleYear: number;
  productId?: number | null;
  productCode?: string | null;
  productSku?: string | null;
  productDescription?: string | null;
  category: ScheduledOrderRuleCategory;
  reason: string;
  targetProductId?: number | null;
  targetProductCode?: string | null;
  targetProductSku?: string | null;
  targetProductDescription?: string | null;
  segmentName?: string | null;
  seriesName?: string | null;
  materialUseName?: string | null;
  brandName?: string | null;
  shipments: ScheduledOrderRuleShipment[];
}

export const scheduledOrderRulesApi = {
  getProducts: (cycleYear: number, includeImmediate = false) =>
    apiClient.get<{ data: ScheduledOrderRuleProduct[] }>(
      `/scheduled-orders/rules/products?cycleYear=${cycleYear}&includeImmediate=${includeImmediate}`,
    ),
  classifyItems: (cycleYear: number, items: unknown[]) =>
    apiClient.post<{ data: ScheduledOrderRuleProduct[] }>(
      "/scheduled-orders/rules/classify",
      { cycleYear, items },
    ),
};
/* Products API (create_product table) */
export const productsApi = {
  getAll: (cycleYear?: number) =>
    apiClient.get<{ data: Product[] }>(
      cycleYear ? `/products?cycleYear=${cycleYear}` : "/products",
    ),
  getById: (id: number) => apiClient.get<Product>(`/products/${id}`),
  getByCode: (code: string) => apiClient.get<Product>(`/products/code/${code}`),
  getActive: (cycleYear?: number) =>
    apiClient.get<{ data: Product[] }>(
      cycleYear ? `/products/active?cycleYear=${cycleYear}` : "/products/active",
    ),
  getByBrand: (brand: string) =>
    apiClient.get<{ data: Product[] }>(`/products/brand/${brand}`),
  getBySegment: (segment: string) =>
    apiClient.get<{ data: Product[] }>(`/products/segment/${segment}`),
  search: (query: string) =>
    apiClient.get<{ data: Product[] }>(
      `/products/search?q=${encodeURIComponent(query)}`,
    ),
  create: (product: Omit<Product, "id" | "created_at" | "updated_at">) =>
    apiClient.post<Product>("/products", product),
  update: (id: number, product: Partial<Product>) =>
    apiClient.put<Product>(`/products/${id}`, product),
  delete: (id: number) => apiClient.delete<any>(`/products/${id}`),
};

/* Clients API (client table with profile_client join) */
export const clientsApi = {
  getAll: () => apiClient.get<{ data: Client[] }>("/clients"),
  getUnique: () =>
    apiClient.get<{ data: Client[] }>("/clients/profile-clients"),
  getSummaries: () => apiClient.get<{ data: any[] }>("/clients/summaries"),
  getQuantityOrders: (id: number) =>
    apiClient.get<{ data: QuantityOrders }>(`/clients/${id}/quantity-orders`),
  getById: (id: number) => apiClient.get<Client>(`/clients/${id}`),
  getByCode: (code: string) => apiClient.get<Client>(`/clients/code/${code}`),
  getByCnpj: (cnpj: number) => apiClient.get<Client>(`/clients/cnpj/${cnpj}`),
  getProductPricing: (clientId: number, cycleYear?: number) =>
    apiClient.get<{ data: ProductPricingBySegment[] }>(
      `/clients/${clientId}/product-pricing${cycleYear ? `?cycleYear=${cycleYear}` : ""}`,
    ),
  search: (query: string) =>
    apiClient.get<{ data: Client[] }>(
      `/clients/search?q=${encodeURIComponent(query)}`,
    ),
  create: (client: Omit<Client, "id" | "created_at" | "updated_at">) =>
    apiClient.post<Client>("/clients", client),
  update: (id: number, client: Partial<Client>) =>
    apiClientIdToken.put<Client>(`/clients/${id}`, client),
  delete: (id: number) => apiClient.delete<any>(`/clients/${id}`),
  summary: () => apiClient.get<{ data: Summary }>(`/clients/summary`),
  validateCode: (code: string, excludeId?: number) =>
    apiClient.get<{ isUnique: boolean }>(
      `/clients/validate/code/${code}${excludeId ? `?excludeId=${excludeId}` : ""
      }`,
    ),
  validateCnpj: (cnpj: number, excludeId?: number) =>
    apiClient.get<{ isUnique: boolean }>(
      `/clients/validate/cnpj/${cnpj}${excludeId ? `?excludeId=${excludeId}` : ""
      }`,
    ),
  getSchoolsByAccount: (accountId: string) =>
    apiClientIdToken.get<{ data: School[] }>(
      `/clients/account/${accountId}/schools`,
    ),
  getCycles: () =>
    apiClient.get<{ cycles: ClientCycleInfo[] }>("/clients/cycle"),
  changeCnpj: (sourceClientId: number, targetClientId: number, reason: string) =>
    apiClientIdToken.post<ChangeClientCnpjResult>(
      `/clients/${sourceClientId}/change-cnpj`,
      { targetClientId, reason },
    ),
};

// Grupos de cliente importados do Magento por CSV (customerGroupAddressFeature). ID token: a importação exige a
// role de administrador.
export const customerGroupsApi = {
  /** Listagem paginada na API (pageSize até 200), ordenada por nome. Sem status, traz só os ativos. */
  list: (params: CustomerGroupListParams = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    if (params.search?.trim()) query.set("search", params.search.trim());
    if (params.status) query.set("status", params.status);
    const suffix = query.toString();
    return apiClientIdToken.get<PagedResult<CustomerGroup>>(`/customer-groups${suffix ? `?${suffix}` : ""}`);
  },
  /** Grupos com ID do Magento que não estão na lista (os "fora do arquivo" da importação). */
  missingFromFile: (magentoGroupIds: number[]) =>
    apiClientIdToken.post<{ data: CustomerGroup[] }>("/admin/customer-groups/missing-from-file", { magentoGroupIds }),
  getById: (id: number) => apiClientIdToken.get<CustomerGroup>(`/customer-groups/${id}`),
  /**
   * Um lote do CSV de grupos (ver csvImportBatches.ts). dryRun = true só devolve a prévia; false grava as linhas
   * válidas do lote. Reenviar um lote já gravado não duplica nada.
   */
  importBatch: (batch: CsvImportBatch, dryRun: boolean, signal?: AbortSignal) =>
    apiClientIdToken.post<CustomerGroupImportResult>(`/admin/customer-groups/import/batches?dryRun=${dryRun}`, batch, {
      signal,
    }),
  /** Resumo da importação de grupos no histórico; uma vez por importId. */
  completeImport: (importId: string, summary: CompleteCustomerGroupImport, signal?: AbortSignal) =>
    apiClientIdToken.post<{ importId: string }>(`/admin/customer-groups/import/${importId}/complete`, summary, { signal }),
  /**
   * Um lote do CSV de clientes do Magento com o grupo de cada um (ver clientGroupImportBatches.ts); liga pelo CNPJ.
   * dryRun = true só devolve a prévia. Reenviar um lote já gravado não duplica nada.
   */
  importClientsBatch: (batch: CsvImportBatch, dryRun: boolean, signal?: AbortSignal) =>
    apiClientIdToken.post<ClientCustomerGroupImportResult>(
      `/admin/customer-groups/clients/import/batches?dryRun=${dryRun}`,
      batch,
      { signal },
    ),
  /**
   * Uma parte do resumo da importação em lotes no histórico. Uma linha por importId: cada parte nova junta as suas
   * rejeitadas; parte repetida não grava de novo.
   */
  completeClientsImport: (importId: string, summary: CompleteClientCustomerGroupImport, signal?: AbortSignal) =>
    apiClientIdToken.post<{ importId: string }>(`/admin/customer-groups/clients/import/${importId}/complete`, summary, {
      signal,
    }),
  create: (group: SaveCustomerGroupDto) =>
    apiClientIdToken.post<CustomerGroup>("/admin/customer-groups", group),
  update: (id: number, group: SaveCustomerGroupDto) =>
    apiClientIdToken.put<CustomerGroup>(`/admin/customer-groups/${id}`, group),
  /** Grupo nunca é apagado: "excluir" na tela é desativar. 409 se houver cliente vinculado. */
  deactivate: (id: number) => apiClientIdToken.post<CustomerGroup>(`/admin/customer-groups/${id}/deactivate`),
  reactivate: (id: number) => apiClientIdToken.post<CustomerGroup>(`/admin/customer-groups/${id}/reactivate`),
  /** Importações confirmadas e alterações de grupos, mais recentes primeiro, paginado na API (pageSize até 200). */
  history: (
    params: {
      customerGroupId?: number;
      /** Parte do código ou do nome do grupo, ou de quem fez, ou o ID do Magento. */
      search?: string;
      action?: CustomerGroupHistoryAction;
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.customerGroupId) query.set("customerGroupId", String(params.customerGroupId));
    if (params.search?.trim()) query.set("search", params.search.trim());
    if (params.action) query.set("action", params.action);
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const suffix = query.toString();
    return apiClientIdToken.get<PagedResult<CustomerGroupHistoryEntry>>(
      `/admin/customer-groups/history${suffix ? `?${suffix}` : ""}`,
    );
  },
};

// Endereços de cobrança e entrega do cliente CNPJ (customerGroupAddressFeature).
export const clientAddressesApi = {
  list: (clientId: number) =>
    apiClientIdToken.get<{ data: ClientAddress[] }>(`/clients/${clientId}/addresses`),
  create: (clientId: number, address: CreateClientAddressDto) =>
    apiClientIdToken.post<ClientAddress>(`/clients/${clientId}/addresses`, address),
  update: (clientId: number, addressId: number, address: UpdateClientAddressDto) =>
    apiClientIdToken.put<ClientAddress>(`/clients/${clientId}/addresses/${addressId}`, address),
  setDefault: (clientId: number, addressId: number) =>
    apiClientIdToken.post<ClientAddress>(`/clients/${clientId}/addresses/${addressId}/set-default`),
  /** Desativação lógica. */
  deactivate: (clientId: number, addressId: number) =>
    apiClientIdToken.delete<ClientAddress>(`/clients/${clientId}/addresses/${addressId}`),
};

// Cliente pessoa física (physicalClientFeature). O CPF completo nunca vai na URL: buscas e verificações são POST com
// o CPF no body, e o CPF completo só vem de revealDocument (que a API registra). As demais respostas trazem cpfMasked.
export type PhysicalClientStatus = "Active" | "Inactive" | "Blocked";
export type PhysicalClientStatusFilter = PhysicalClientStatus | "All";
export type PhysicalClientOrigin = "Portal" | "Magento" | "Erp";

export interface PhysicalClientListItem {
  id: number;
  /** ***.456.789-** */
  cpfMasked: string;
  fullName: string;
  email?: string | null;
  mobilePhone?: string | null;
  status: PhysicalClientStatus;
  customerGroup?: CustomerGroupSummary | null;
  origin: PhysicalClientOrigin;
  erpCode?: string | null;
}

export interface PhysicalClient extends PhysicalClientListItem {
  phone?: string | null;
  customerGroupId?: number | null;
  externalId?: string | null;
  sourceCreatedAt?: string | null;
  addresses: PhysicalClientAddress[];
  defaultBillingAddress?: PhysicalClientAddress | null;
  defaultShippingAddress?: PhysicalClientAddress | null;
  createdAt: string;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Cadastro: origem sempre Portal; sem status, nasce Active. Endereços são cadastrados depois, no detalhe. */
export interface CreatePhysicalClientDto {
  cpf: string;
  fullName: string;
  email?: string | null;
  mobilePhone?: string | null;
  phone?: string | null;
  status?: PhysicalClientStatus;
  customerGroupId?: number | null;
  erpCode?: string | null;
}

/**
 * Edição: substitui TODOS os campos editáveis (ausente ou null limpa o valor; customerGroupId null remove o grupo).
 * CPF e situação não são editáveis aqui.
 */
export interface UpdatePhysicalClientDto {
  fullName: string;
  email: string | null;
  mobilePhone: string | null;
  phone: string | null;
  customerGroupId: number | null;
  erpCode: string | null;
  comment?: string | null;
}

/** Corpo de POST /physical-clients/search. Use cpf OU query, nunca os dois (ver physicalClientSearch.ts). */
export interface PhysicalClientSearch {
  query?: string;
  /** Só dígitos; busca exata. */
  cpf?: string;
  status?: PhysicalClientStatusFilter;
  customerGroupId?: number;
  page: number;
  pageSize: number;
}

export interface CpfCheckResult {
  exists: boolean;
  physicalClientId?: number | null;
  fullName?: string | null;
  status?: PhysicalClientStatus | null;
}

/** CPF completo (123.456.789-09). Nunca guarde em cache, estado global, URL, console ou toast. */
export interface PhysicalClientDocument {
  cpf: string;
  accessedAt: string;
}

export interface PhysicalClientDocumentAccess {
  id: number;
  accessedBy: string;
  accessedAt: string;
  reason?: string | null;
}

export type PhysicalClientHistoryAction =
  | "Create"
  | "Update"
  | "StatusChange"
  | "AddressCreate"
  | "AddressUpdate"
  | "AddressSetDefault"
  | "AddressDeactivate";

export interface PhysicalClientHistoryEntry {
  id: number;
  action: PhysicalClientHistoryAction;
  comment?: string | null;
  previous?: Record<string, unknown> | null;
  current?: Record<string, unknown> | null;
  performedBy: string;
  performedAt: string;
}

export const physicalClientsApi = {
  search: (search: PhysicalClientSearch) =>
    apiClientIdToken.post<PagedResult<PhysicalClientListItem>>("/physical-clients/search", search),
  getById: (id: number) => apiClientIdToken.get<PhysicalClient>(`/physical-clients/${id}`),
  create: (client: CreatePhysicalClientDto) => apiClientIdToken.post<PhysicalClient>("/physical-clients", client),
  update: (id: number, client: UpdatePhysicalClientDto) =>
    apiClientIdToken.put<PhysicalClient>(`/physical-clients/${id}`, client),
  changeStatus: (id: number, status: PhysicalClientStatus, comment: string) =>
    apiClientIdToken.post<PhysicalClient>(`/physical-clients/${id}/status`, { status, comment }),
  /** Pré-checagem de duplicidade; o CPF vai no body. */
  checkCpf: (cpf: string) => apiClientIdToken.post<CpfCheckResult>("/physical-clients/check-cpf", { cpf }),
  /** CPF completo. A API registra o acesso (quem, quando e o motivo). */
  revealDocument: (id: number, reason?: string) =>
    apiClientIdToken.post<PhysicalClientDocument>(`/physical-clients/${id}/document`, { reason: reason || null }),
  getDocumentAccessLog: (id: number) =>
    apiClientIdToken.get<{ data: PhysicalClientDocumentAccess[] }>(`/physical-clients/${id}/document-access-log`),
  getHistory: (id: number) =>
    apiClientIdToken.get<{ data: PhysicalClientHistoryEntry[] }>(`/physical-clients/${id}/history`),
};

// Endereços do cliente pessoa física: mesma forma de clientAddressesApi (ver useAddressBook).
export const physicalClientAddressesApi = {
  list: (physicalClientId: number) =>
    apiClientIdToken.get<{ data: PhysicalClientAddress[] }>(`/physical-clients/${physicalClientId}/addresses`),
  create: (physicalClientId: number, address: CreateClientAddressDto) =>
    apiClientIdToken.post<PhysicalClientAddress>(`/physical-clients/${physicalClientId}/addresses`, address),
  update: (physicalClientId: number, addressId: number, address: UpdateClientAddressDto) =>
    apiClientIdToken.put<PhysicalClientAddress>(`/physical-clients/${physicalClientId}/addresses/${addressId}`, address),
  setDefault: (physicalClientId: number, addressId: number) =>
    apiClientIdToken.post<PhysicalClientAddress>(`/physical-clients/${physicalClientId}/addresses/${addressId}/set-default`),
  /** Desativação lógica. */
  deactivate: (physicalClientId: number, addressId: number) =>
    apiClientIdToken.delete<PhysicalClientAddress>(`/physical-clients/${physicalClientId}/addresses/${addressId}`),
};

/** Exportação dos pedidos programados de um ciclo (GET /scheduled-orders/all?mode=export, .xlsx com abas Pedidos e Itens). */
export const scheduledOrdersExportApi = {
  exportCycle: (cycleYear: number) =>
    apiClientIdToken.get<Blob>(`/scheduled-orders/all?mode=export&cycleYear=${cycleYear}`, {
      responseType: "blob",
    }),
};

export const scheduledOrderCutoffPoliciesApi = {
  getAll: () =>
    apiClientIdToken.get<{ data: ScheduledOrderCutoffPolicy[] }>(
      "/admin/scheduled-order-cutoff-policies",
    ),
};

/* Payment Plans API (catálogo de planos de pagamento) */
export const paymentPlansApi = {
  getAll: async (): Promise<PaymentPlan[]> => {
    const data = await apiClient.get<{ data: PaymentPlan[] } | PaymentPlan[]>(
      "/payment-plans",
    );
    return Array.isArray(data) ? data : data?.data ?? [];
  },
};

export const paymentConfigurationsApi = {
  getAll: async (): Promise<PaymentConfiguration[]> => {
    const response = await apiClient.get<{ data: PaymentConfiguration[] }>(
      "/payment-configuration",
    );
    return response?.data ?? [];
  },
  /** Exporta a listagem em .xlsx (mesma rota da listagem, com o parâmetro export). */
  exportXlsx: () =>
    apiClient.get<Blob>("/payment-configuration?export=true", { responseType: "blob" }),
  /** POST /payment-configuration: sempre cria um novo registro (não faz upsert). */
  create: async (configuration: Omit<PaymentConfiguration, "$id">): Promise<void> => {
    await apiClient.post("/payment-configuration", {
      payments: [configuration],
    });
  },
  /**
   * DELETE /payment-configuration: envia apenas o $id para remover exatamente
   * um registro (enviar a condição removeria todas as linhas com o mesmo nome).
   */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete("/payment-configuration", {
      data: { payments: [{ $id: id }] },
    });
  },
};

export interface ClientCycleInfo {
  year: number;
  status: CommercialCycleStatus;
}

export type ScheduledItemType = {
  productId: number;
  productSku: string;
  contractualQuantity: number;
  discount: number;
  adjustment: number;
  isActive: boolean;
};
export interface ScheduledItems {
  clientCode: string;
  items: ScheduledItemType[];
  cycleYear?: number;
}

export type Products = {
  profileClientId: number;
  clientId: number;
  productId: number;
  materialUseName: "Aluno" | "Professor";
  productCode: string;
  productDescription: string;
  productSchoolYear: string;
  contractualQuantity: number;
  discount: number;
  adjustment: number;
  productPrice: number;
  priceWithoutDiscount?: number;
  priceWithDiscount?: number;
  totalWithoutDiscount: number;
  totalWithDiscount: number;
  segmentName: string;
  seriesName: string;
  isActive: boolean;
  isOptional?: boolean;
  isShipmentExempt?: boolean;
  shipmentExemptionApplies?: boolean;
  productSku: string;
  brandName?: string;
  productTypeName?: string;
  deliveryTypeName?: string;
  content?: string;
};

export interface ProfileDetails {
  materialUseName: "Aluno" | "Professor";
  products: Products[];
}

/* Profile Client API (profile_client table) */
export const profileClientApi = {
  getProfileDetails: (clientId: number, cycleYear?: number) =>
    apiClient.get<{ data: ProfileDetails[] }>(
      `/clients/${clientId}/profile-details${cycleYear ? `?cycleYear=${cycleYear}` : ""}`,
    ),
  getByClientCode: (clientCode: string, cycleYear?: number) =>
    apiClient.get<{ data: ProfileClient[] }>(
      `/clients/profile/${clientCode}${cycleYear ? `?cycleYear=${cycleYear}` : ""}`
    ),
  getAll: () => apiClient.get<{ data: ProfileClient[] }>("/clients/profiles"),
  getPrfileHistory: (id: number) =>
    apiClient.get<{ data: ProfileHistory[] }>(`/clients/${id}/profile-history`),
  addProductProfileClient: (value: ProfileClientAddProductDto) =>
    apiClientIdToken.post<any>("/profile-client", value),
  profileClientDeactivate: (id: string, content: { content: string }) =>
    apiClientIdToken.post<any>(`/profile-client/${id}/deactivate`, content),
  profileClientActivate: (id: string, content: { content: string }) =>
    apiClient.post<any>(`/profile-client/${id}/activate`, content),
  create: (profile: Omit<ProfileClient, "id" | "created_at" | "updated_at">) =>
    apiClient.post<ProfileClient>("/clients/profiles", profile),
  update: (id: number, profile: Partial<ProfileClient>) =>
    apiClient.put<ProfileClient>(`/clients/profiles/${id}`, profile),
  delete: (id: number) => apiClient.delete<any>(`/clients/profiles/${id}`),
  scheduledItems: (body: ScheduledItems) => {
    return apiClient.post<any>(`/profile-client/scheduled-items`, {
      clientCode: body.clientCode,
      items: body.items,
      cycleYear: body.cycleYear,
    });
  },
};

const toBackendOrder = (orderData: CreateOrderDto) => {
  const date = new Date();
  return {
    currentMonth: date.getMonth() + 1,
    currentYear: date.getFullYear(),
    comment: orderData.backofficeReason,
    orderNumber: orderData.order_number,
    clientName: orderData.client_name,
    clientCode: orderData.client_code,
    createdBy: orderData.created_by,
    totalAmount: orderData.total_amount,
    subtotalAmount: orderData.subtotal_amount,
    cycleCode: orderData.cycle_code,
    couponCode: orderData.coupon_code,
    couponDiscount: orderData.coupon_discount || 0,
    couponDiscountAmount: orderData.coupon_discount_amount || 0,
    couponReservationId: orderData.coupon_reservation_id || undefined,
    totalItems: orderData.total_items,
    shippingAmount: orderData.shipping_amount,
    isScheduled: orderData.isScheduled,
    quantityInstallment: orderData.quantityInstallment,
    paymentConfiguration: orderData.paymentConfiguration || null,
    sourcePurchaseOrderId: orderData.sourcePurchaseOrderId || null,
    shippingAddressId: orderData.shipping_address_id ?? undefined,
    clientCompanyName: orderData.client_company_name,
    clientCnpj: orderData.client_cnpj,
    clientCustomerGroup: orderData.client_customer_group,
    clientAddress: orderData.client_address,
    clientState: orderData.client_state,
    clientCity: orderData.client_city,
    clientPhone: orderData.client_phone,
    clientEmail: orderData.client_email,
    deliveryTypeId: orderData.deliveryTypeId,
    items: orderData.coupon_reservation_id ? (orderData.coupon_items || []) : (orderData.items || []),
    customer: {
      clientCodeCnx: orderData.client_code,
      clientCompanyName: orderData.client_name,
    },
  };
};

const toBackendOrderItems = (items: OrderItem[]) => items.map((item: any) => ({
  productId: item.product_id,
  codigoProduto: item.codigo_produto,
  descricaoProduto: item.descricao_produto,
  preco: item.preco,
  quantidade: item.quantidade,
  desconto: item.desconto,
  valorTotal: item.valor_total,
  deliveryTypeId: item.delivery_type_id,
  brandId: item.brand_id,
  productSeries: item.product_series?.toString(),
  segmentName: item.segment_name?.toString(),
  brandName: item.brand_name?.toString(),
  productSchoolYear: item.product_school_year,
  productSku: item.product_sku ? item.product_sku.toString() : null,
  quantityContracted: item.quantity_contracted,
  bonifiedQuantity: item.bonified_quantity || 0,
}));

/* Orders API (using PostgreSQL stored procedures) */
export interface CustomerPortalSummary {
  /** Ano civil (Brasília) em que os pedidos foram feitos, de qualquer ciclo. */
  year: number;
  availableYears: number[];
  /** Ciclos com pedidos da unidade ou abertos a pedidos. */
  availableCycleYears: number[];
  /** Produtos + frete, como na página de Pedidos. */
  ordersAmount: number;
  returnsAndExemptionsAmount: number;
  invoicesAmount: number | null;
  openAmount: number;
  paidAmount: number;
  financialAvailable: boolean;
  hasLinkedInvoices: boolean;
  financialPeriodStart: string;
  financialPeriodEnd: string;
  unmatchedFinancialEntries: number;
  /** Todos os pedidos que contam da unidade, para o gráfico filtrar por ano ou por ciclo. */
  orders: CustomerPortalOrder[];
}

export interface CustomerPortalOrder {
  /** Data do pedido (UTC). */
  date: string;
  /** Produtos + frete. */
  amount: number;
  /** Ano civil do pedido em Brasília. */
  year: number;
  /** Ciclo comercial; nulo quando o pedido não tem cycle_code nem ano letivo nos itens. */
  cycleYear: number | null;
}

export const customerPortalApi = {
  getSummary: (clientCode: string, year: number) =>
    apiClientIdToken.get<{ data: CustomerPortalSummary }>(
      `/customer-portal/summary?clientCode=${encodeURIComponent(clientCode)}&year=${year}`,
    ),
};

// Ambiente do ERP Dynamics usado nas consultas: configuração global (só fora de produção; qualquer usuário autenticado).
export const dynamicsEnvironmentApi = {
  get: () =>
    apiClientIdToken.get<{ environment: "hml" | "prd" }>("/system-configuration/dynamics-environment"),
  set: (environment: "hml" | "prd") =>
    apiClientIdToken.put<{ environment: "hml" | "prd" }>("/system-configuration/dynamics-environment", { environment }),
};

export const ordersApi = {
  invoiceStatus: (orderNumber: string) =>
    apiClient.get<InvoiceStatus>(`/orders/invoice-status/${orderNumber}`),
  getAll: () => apiClient.get<{ data: Order[] }>("/orders"),
  getOrderHistory: () =>
    apiClientIdToken.get<{ data: Order[] }>("/orders/history"),
  getById: (id: string) => apiClient.get<Order>(`/orders/${id}`),
  getByNumber: (orderNumber: string) =>
    apiClient.get<Order>(`/orders/number/${orderNumber}`),
  getByClient: (clientCode: string) =>
    apiClient.get<{ data: Order[] }>(`/orders/client/${clientCode}`),
  getStatus: (orderNumber?: string) =>
    apiClient.get<OrderStatus>(`/orders/order-status/${orderNumber}`),
  getByStatus: (status: string) =>
    apiClient.get<{ data: Order[] }>(`/orders/status/${status}`),
  getRecent: (count = 10) =>
    apiClient.get<{ data: Order[] }>(`/orders/recent?count=${count}`),
  getByDateRange: (startDate: string, endDate: string) =>
    apiClient.get<{ data: Order[] }>(
      `/orders/date-range?startDate=${startDate}&endDate=${endDate}`,
    ),
  // Create order using PostgreSQL stored procedure
  create: (orderData: CreateOrderDto) => {
    // Sempre o ID token (carrega o e-mail): o back usa o e-mail do usuário como solicitante do chamado do Zendesk
    // e como dono da reserva de cupom (mesmo token da reserva em couponApi), igual ao cancelamento e ao checklist.
    return apiClientIdToken.post<{ order_id: string }>(
      "/orders/create-order",
      toBackendOrder(orderData),
    );
  },

  createItemsBatch: (items: { order_id: string; items: OrderItem[] }) => {
    // Convert snake_case to PascalCase for backend compatibility
    const backendData = {
      orderId: items.order_id,
      items: toBackendOrderItems(items.items),
    };
    console.log(
      "🚀 Backend data being sent to API:",
      JSON.stringify(backendData, null, 2),
    );
    return apiClient.post<{ success: boolean }>(
      "/orders/create-order-items",
      backendData,
    );
  },

  delete: (id: string) => apiClient.delete<any>(`/orders/${id}`),
  validateOrderNumber: (orderNumber: string) =>
    apiClient.get<{ isUnique: boolean }>(
      `/orders/validate/number/${orderNumber}`,
    ),

  // Get total orders amount with aggregated data
  getTotalOrdersAmount: () =>
    apiClient.get<{ data: TotalOrdersAmount[] }>("/orders/total-amount-orders"),

  // Resend webhook for an existing order
  // Rota só de administrador (ID token), como /orders/history.
  resendWebhook: (orderNumber: string) =>
    apiClientIdToken.post<ResendWebhook>(`/orders/resend-webhook/${orderNumber}`),

  // Andamento da integração direta do pedido com o ERP (erpDirectIntegrationFeature; rota de administrador)
  getErpIntegration: (id: string) =>
    apiClientIdToken.get<ErpIntegrationDetails>(`/orders/${id}/erp-integration`),

  // Opções da integração direta com o ERP: configuração global em system_configuration (só fora de produção; qualquer usuário autenticado).
  getErpIntegrationSettings: () =>
    apiClientIdToken.get<ErpIntegrationSettingsValues>("/orders/erp-integration-settings"),
  // Só o que for informado é alterado.
  setErpIntegrationSettings: (change: Partial<ErpIntegrationSettingsValues>) =>
    apiClientIdToken.put<ErpIntegrationSettingsValues>("/orders/erp-integration-settings", change),

  // Cancelamento de pedido (ver .claude/docs/frontend_order_cancellation.md)
  cancel: (id: string, reason?: string) =>
    apiClientIdToken.post<Order>(`/orders/${id}/cancel`, reason?.trim() ? { reason: reason.trim() } : {}),
  getCancellationTicket: (id: string) =>
    apiClientIdToken.get<CancellationTicket>(`/orders/${id}/cancellation-ticket`),

  // Solicitações de "Ajuda com o meu pedido". Token com e-mail: o back grava quem solicitou e o repassa ao Zendesk.
  getRequestTypes: () =>
    apiClientIdToken.get<OrderRequestType[]>("/orders/request-types"),
  getRequests: (id: string) =>
    apiClientIdToken.get<OrderSupportRequest[]>(`/orders/${id}/requests`),
  // O header explícito impede o axios de serializar o FormData como JSON (o padrão do ApiClient);
  // no navegador ele é trocado pelo multipart com boundary.
  getRequestComments: (id: string, zendeskTicketId: number) =>
    apiClientIdToken.get<OrderSupportRequestConversation>(`/orders/${id}/requests/${zendeskTicketId}/comments`),
  // O arquivo vem pela API (que busca no Zendesk): a URL do Zendesk nunca chega ao navegador.
  downloadRequestCommentAttachment: (id: string, zendeskTicketId: number, attachmentId: number) =>
    apiClientIdToken.get<Blob>(`/orders/${id}/requests/${zendeskTicketId}/comments/attachments/${attachmentId}`, {
      responseType: "blob",
    }),
  createAttachmentUploadUrl: (id: string, request: CreateSupportAttachmentUploadDto) =>
    apiClientIdToken.post<SupportAttachmentUpload>(`/orders/${id}/requests/attachments/upload-url`, request),
  createRequest: (id: string, request: CreateOrderSupportRequestDto) =>
    apiClientIdToken.post<OrderSupportRequest>(`/orders/${id}/requests`, toSupportRequestFormData(request), {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

/* Brands API (brand table) - relacionado a products */
export const brandsApi = {
  getAll: () => apiClient.get<{ data: Brand[] }>("/products/brands"),
  getById: (id: number) => apiClient.get<Brand>(`/products/brands/${id}`),
  create: (brand: { name: string }) =>
    apiClient.post<Brand>("/products/brands", brand),
  update: (id: number, brand: { name: string }) =>
    apiClient.put<Brand>(`/products/brands/${id}`, brand),
  delete: (id: number) => apiClient.delete<any>(`/products/brands/${id}`),
};

export interface Invoicing {
  id: number;
  invoicingName: string;
  deliveryTypeId: number;
  invoicingQtd: number;
  currentMonth: number | null;
  currentYear: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInvoicingDto {
  invoicingName: string;
  deliveryTypeId: number;
  invoicingQtd: number;
  currentMonth: number;
  currentYear: number;
}
export interface InvoicingSummary {
  deliveryTypeId: number;
  totalInvoicingQtd: number;
}

export interface InstallmentOptionConfiguration {
  paymentConfigurationId: string;
  installmentQuantity: number;
  /** Alias legado; novos consumidores devem usar installmentQuantity. */
  option: number;
  condition: string;
  paymentDay: string;
  months: string;
}

export interface InstallmentOptionsDto {
  paymentPlan?: PaymentPlan | null;
  deliveryTypeId: number;
  maxInstallments: number;
  startMonth: number;
  startYear: number;
  fullPaymentOnly: boolean;
  availableOptions: number[];
  optionsConfiguration: InstallmentOptionConfiguration[];
}

export interface DelinquencyStatus {
  delinquencyStatus: 0 | 2; // 0 para Adimplente, 2 para Inadimplente
  delinquencyStatusDescription: string;
}

export const invoicingApi = {
  getAll: () => apiClient.get<{ data: Invoicing[] }>("/invoicing"),
  create: (data: CreateInvoicingDto) =>
    apiClient.post<Invoicing>("/invoicing", data),
  update: (id: number, data: CreateInvoicingDto) =>
    apiClient.put<Invoicing>(`/invoicing/${id}`, data),
  delinquencyStatus: (cnpj: string) =>
    apiClient.get<{ data: DelinquencyStatus }>(
      `/invoicing/delinquency-status?cnpj=${cnpj}`,
    ),
  summary: (month, year) =>
    apiClient.get<{ data: Invoicing[] }>(
      `/invoicing/summary?month=${month}&year=${year}`,
    ),
  installmentOptions: (month: number, year: number, cnpj: string, cycleYear?: number | null) =>
    apiClient.get<{ data: InstallmentOptionsDto[] }>(
      `/invoicing/installment-options?month=${month}&year=${year}&cnpj=${cnpj}${cycleYear ? `&cycleYear=${cycleYear}` : ""}&_t=${Date.now()}`,
    ),
};

/* Delivery Types API (delivery_type table) - relacionado a orders */
export const deliveryTypesApi = {
  getAll: () =>
    apiClient.get<{ data: DeliveryType[] }>("/orders/delivery-types"),
  getById: (id: number) =>
    apiClient.get<DeliveryType>(`/orders/delivery-types/${id}`),
  create: (deliveryType: { name: string; number: number }) =>
    apiClient.post<DeliveryType>("/orders/delivery-types", deliveryType),
  update: (id: number, deliveryType: Partial<DeliveryType>) =>
    apiClient.put<DeliveryType>(`/orders/delivery-types/${id}`, deliveryType),
  delete: (id: number) => apiClient.delete<any>(`/orders/delivery-types/${id}`),
};

/* Segments API - relacionado a products */
export const segmentsApi = {
  getAll: () => apiClient.get<string[]>("/products/segments"),
  getUsageTypes: () => apiClient.get<string[]>("/products/usage-types"),
  getSeries: () => apiClient.get<string[]>("/products/series"),
};

/* Health check */
export const healthApi = {
  check: () => apiClient.get<any>("/health"),
};

/* Order Drafts API */
export const orderDraftsApi = {
  get: (clientCode: string) =>
    apiClient.get<OrderDraft>(`/order-drafts?clientCode=${clientCode}`),
  save: (request: SaveOrderDraftRequest, userEmail?: string) =>
    apiClient.post<OrderDraft>(
      `/order-drafts${userEmail ? `?userEmail=${encodeURIComponent(userEmail)}` : ""
      }`,
      request,
    ),
  delete: (clientCode: string) =>
    apiClient.delete<void>(`/order-drafts?clientCode=${clientCode}`),
};

export interface FinancialSummary {
  total: number;
  emAberto: number;
  liquidado: number;
  vencidos: number;
}

export interface BoletoLine {
  $id: string;
  installments: string;
  dueDate: string;
  transDate: string;
  orderId: string;
  invoiceId: string;
  oldAmount: number;
  newAmount: number;
  paymentStatus: number;
  transactionType: number;
  typeLabel: "Expired" | "Discount" | "Return" | "Pedido" | "Acordo";
  salesOrderId: string;
  magentoOrderId: string;
  dynamicsSalesId: string;
  orderNumber: string;
  isIsencao: boolean;
  isDevolucao: boolean;
}

export interface BoletoTransaction {
  $id: string;
  cnpjCpf: string;
  monthYear: string;
  dueDate: string;
  oldAmount: number;
  newAmount: number;
  paymentStatus: number;
  statusLabel: string;
  hasCorrection: boolean;
  hasBoleto: boolean;
  parmJournalTransRecId: number;
  /** RecId do boleto no Dynamics. `null` quando a transação não tem boleto. */
  recIdBoleto?: number | null;
  parmBltAceite?: string;
  parmBltAlunoNome?: string;
  parmBltAtualizouHtml?: number;
  parmBltBankCode?: string;
  parmBltCedenteCnpjCpf?: string;
  parmBltCedenteCodigo?: string;
  parmBltCedenteNome?: string;
  parmBltCodBarras?: string;
  parmBltCodigoDv?: string;
  parmBltCodigoOcorrencia?: string;
  parmBltCodigoTransmissao?: string;
  parmBltContaBancariaAgencia?: string;
  parmBltContaBancariaCarteiraPadrao?: string;
  parmBltContaBancariaConta?: string;
  parmBltContaBancariaDigitoAgencia?: string;
  parmBltContaBancariaDigitoConta?: string;
  parmBltContaBancariaMensagemFixaTopoBoleto?: string;
  parmBltContaBancariaOperacaoConta?: string;
  parmBltContaBancariaTipoCarteiraPadrao?: number;
  parmBltContaBancariaTipoDocumento?: number;
  parmBltContaBancariaTipoFormaCadastramento?: number;
  parmBltContaBancariaTipoImpressaoBoleto?: number;
  parmBltContaBancariaVariacaoCarteiraPadrao?: string;
  parmBltDataEmissao?: string;
  parmBltDataVencimento?: string;
  parmBltDescricaoOcorrencia?: string;
  parmBltDetalheDaFatura?: string;
  parmBltEnderecoLogradouroBairro?: string;
  parmBltEnderecoLogradouroCep?: string;
  parmBltEnderecoLogradouroCidade?: string;
  parmBltEnderecoLogradouroComplemento?: string;
  parmBltEnderecoLogradouroEndereco?: string;
  parmBltEnderecoLogradouroNumero?: string;
  parmBltEnderecoLogradouroUf?: string;
  parmBltEnderecoSacadoBairro?: string;
  parmBltEnderecoSacadoCep?: string;
  parmBltEnderecoSacadoCidade?: string;
  parmBltEnderecoSacadoComplemento?: string;
  parmBltEnderecoSacadoEndereco?: string;
  parmBltEnderecoSacadoNumero?: string;
  parmBltEnderecoSacadoUf?: string;
  parmBltEspecieDocumento?: number;
  parmBltGmPacoteDeCursoDescricao?: string;
  parmBltGmTurnoNome?: string;
  parmBltGrupoDemonstrativo?: string;
  parmBltMensagemInformacaoAdicional?: string;
  parmBltMensagemInstrucoesCaixa?: string;
  parmBltMsgReferenciaPedido?: string;
  parmBltNumeroControleParticipante?: string;
  parmBltNumeroDocumento?: string;
  parmBltSacadoCnpjCpf?: string;
  parmBltSacadoNome?: string;
  parmBltValorTitulo?: number;
  linhaDigitavel?: string;
  parmBase64PdfBoleto?: string;
  lines: BoletoLine[];
}

/** `GET /api/boletos` devolve a resposta do Dynamics sem mapear — o RecId do boleto vem com o nome cru. */
export interface RawBoletoTransaction extends BoletoTransaction {
  RECID_BOLETO?: number | null;
}

/** `GET /api/boletos/{recIdBoleto}/billet`. Ver .claude/docs/frontend_boleto_availability.md, seção 2. */
export interface BilletInvoice {
  recIdBoleto: number;
  disponivel: boolean;
  billet: string;
}

export interface ClosedTransactions {
  $id: string;
  monthYear: string;
  dueDate: string;
  amount: number;
  invoiceId: string;
  settleDate: string;
  isIsencao: boolean;
  isDevolucao: boolean;
  OrderNumber: string | null;
}

export interface ReturnTransactionsLines {
  $id: string;
  orderId: string;
  InvoiceId: string;
  transDate: string;
  dueDate: string;
  amount: number;
}
export interface ReturnTransactions {
  $id: string;
  InvoiceId: string;
  transDate: string;
  amount: number;
  paymentStatus: number;
  lines: ReturnTransactionsLines[];
}

/* Boletos API */
export const boletosApi = {
  get: (cnpjCpf: string | number, date: { to: string; from: string }) =>
    apiClient.get<{
      data: {
        customerTransactionsList: RawBoletoTransaction[];
        summary: FinancialSummary;
      };
    }>(
      `/boletos?cnpjCpf=${cnpjCpf}&dataInicio=${date.from}&dataFim=${date.to}`,
    ),
  getReturnTransactions: (
    cnpjCpf: string | number,
    date: { to: string; from: string },
  ) => {
    return apiClient.get<{
      data: {
        customerTransactionsList: ReturnTransactions[];
      };
    }>(
      `/boletos/returned-invoices?cnpjCpf=${cnpjCpf}&dataInicio=${date.from}&dataFim=${date.to}`,
    );
  },
  getClosedTransactions: (
    cnpjCpf: string | number,
    date: { to: string; from: string },
  ) => {
    return apiClient.get<{
      data: {
        customerTransactionsClosedList: ClosedTransactions[];
      };
    }>(
      `/boletos/closed-invoices?company=CNX&cnpjCpf=${cnpjCpf}&dataInicio=${date.from}&dataFim=${date.to}`,
    );
  },
  getFinancialDetails: (
    cnpjCpf: string | number,
    date: { to: string; from: string },
  ) =>
    apiClient.get<{
      data: {
        customerTransactionsList: BoletoTransaction[];
        summary: FinancialSummary;
      };
    }>(
      `/boletos/financial-details?cnpjCpf=${cnpjCpf}&dataInicio=${date.from}&dataFim=${date.to}`,
    ),
  status: (status: string, cnpjCpf: string | number) =>
    apiClient.get(`/boletos/${status}/status?cnpjCpf=${cnpjCpf}`),
  getData: (
    boleto: string | number,
    cnpjCpf: string | number,
    date: { to: string; from: string },
  ) =>
    apiClient.get<{ data: BoletoData }>(
      `/boletos/${boleto}/data?cnpjCpf=${cnpjCpf}&dataInicio=${date.from}&dataFim=${date.to}`,
    ),
  getBilletInvoice: (recIdBoleto: number) =>
    apiClient.get<{ data: BilletInvoice }>(`/boletos/${recIdBoleto}/billet`),
};

/* Simulated Orders API */
export const simulatedOrdersApi = {
  getAll: () => apiClient.get<{ data: SimulatedOrder[] }>("/simulated-orders"),
  getById: (id: number) =>
    apiClient.get<SimulatedOrder>(`/simulated-orders/${id}`),
  create: (data: CreateSimulatedOrder) =>
    apiClient.post<SimulatedOrder>("/simulated-orders", data),
  update: (id: number, data: Partial<CreateSimulatedOrder>) =>
    apiClient.put<SimulatedOrder>(`/simulated-orders/${id}`, data),
  delete: (id: number) => apiClient.delete<void>(`/simulated-orders/${id}`),
};

export const lexSchoolsApi = {
  getSchoolsByAccount: async (): Promise<School[]> => {
    const user = await userManager.getUser();
    const accountId = user?.profile?.account_id;
    if (!accountId) {
      throw new Error("Account ID not found in session");
    }
    return lexApiClient.get<School[]>(
      `/licenses/accounts/${accountId}/schools`,
    );
  },
};

export interface CsatRatingPayload {
  feedbackProvided: boolean;
  rating?: number;
  commentImprovement?: string | null;
  commentOverall?: string | null;
  email?: string;
}

export const csatApi = {
  submitRating: async (orderNumber: string, payload: CsatRatingPayload) => {
    const user = await userManager.getUser();
    payload.email = user?.profile?.email || "";

    return apiClient.post<{ id: string }>(
      `/csat/ratings/${orderNumber}`,
      payload,
    );
  },
};

/* ─── Client Adjustment (client_adjustment table) ─────────────────────────── */

export interface ClientForAdjustment {
  clientId: number;
  clientCodeCnx: string;
  clientCompanyName: string;
  clientFantasyName: string;
  clientCnpj: string;
  clientState: string;
  clientCity: string;
  clientCustomerGroup?: string;
  quantityOfStudents?: number;
  isInadimplente?: boolean;
}

export interface ClientAdjustmentRecord {
  id?: number;
  clientId: number;
  /** @deprecated use cycleYear */
  year?: number;
  cycleYear: number;
  adjustment: number;
  comment?: string;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface SaveClientAdjustmentDto {
  cycleYear: number;
  adjustment: number;
  comment: string;
}

export interface BulkAdjustmentItemDto {
  clientId: number;
  adjustment: number;
  comment?: string;
}

export interface BulkClientAdjustmentDto {
  cycleYear: number;
  comment: string;
  items: BulkAdjustmentItemDto[];
}

export interface ClientAdjustmentHistoryDto {
  id: number;
  clientId: number;
  cycleYear: number;
  adjustment: number;
  comment?: string;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface GetClientAdjustmentsByYearResponse {
  data: ClientAdjustmentHistoryDto[];
}

export const clientAdjustmentApi = {
  /** GET /clients — lista todos os clientes */
  getAllClients: async (): Promise<ClientForAdjustment[]> => {
    const { data } = await apiClient.get<any>("/clients");
    const rawList: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
        ? data.data
        : [];
    return rawList.map((item) => ({
      clientId: item.clientId ?? item.id,
      clientCodeCnx: item.clientCodeCnx ?? item.codigo_cnx ?? "",
      clientCompanyName: item.clientCompanyName ?? item.nome_cliente ?? "",
      clientFantasyName:
        item.clientFantasyName ?? item.clientCompanyName ?? item.nome_cliente ?? "",
      clientCnpj: item.clientCnpj ?? item.cnpj ?? "",
      clientState: item.clientState ?? item.estado ?? "",
      clientCity: item.clientCity ?? item.cidade ?? "",
      clientCustomerGroup: item.clientCustomerGroup ?? "",
      quantityOfStudents: item.quantityOfStudents ?? 0,
      isInadimplente: item.isInadimplente ?? false,
    }));
  },

  /** GET /clients/{clientId}/adjustments — histórico de reajustes do cliente */
  getAdjustments: async (clientId: number): Promise<ClientAdjustmentRecord[]> => {
    const { data } = await apiClient.get<any>(`/clients/${clientId}/adjustments`);
    const rawList: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
        ? data.data
        : [];
    return rawList.map((item) => ({
      id: item.id,
      clientId: item.clientId ?? clientId,
      year: item.cycleYear ?? item.year ?? 0,
      cycleYear: item.cycleYear ?? item.year ?? 0,
      adjustment: item.adjustment ?? 0,
      comment: item.comment ?? "",
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    }));
  },

  /** GET /clients/{clientId}/adjustments/{year} — reajuste de um ciclo específico */
  getAdjustmentByYear: async (
    clientId: number,
    year: number,
  ): Promise<ClientAdjustmentRecord | null> => {
    const { data } = await apiClient.get<any>(
      `/clients/${clientId}/adjustments/${year}`,
    );
    const raw = data && "data" in data ? data.data : data;
    if (!raw) return null;
    return {
      id: raw.id,
      clientId: raw.clientId ?? clientId,
      year: raw.cycleYear ?? raw.year ?? year,
      cycleYear: raw.cycleYear ?? raw.year ?? year,
      adjustment: raw.adjustment ?? 0,
      comment: raw.comment ?? "",
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    };
  },

  /** POST /clients/{clientId}/adjustments — criar/atualizar reajuste individual */
  saveAdjustment: (
    clientId: number,
    payload: SaveClientAdjustmentDto,
  ) =>
    apiClientIdToken.post<any>(`/clients/${clientId}/adjustments`, {
      cycleYear: payload.cycleYear,
      adjustment: payload.adjustment,
      comment: payload.comment,
    }),

  /** DELETE /clients/{clientId}/adjustments/{year}?comment={comment} */
  deleteAdjustment: (clientId: number, year: number, comment?: string) => {
    const query = comment ? `?comment=${encodeURIComponent(comment)}` : "";
    return apiClientIdToken.delete<void>(
      `/clients/${clientId}/adjustments/${year}${query}`,
    );
  },

  /** GET /clients/adjustments/year/{year} — busca todos os reajustes de um determinado ano/ciclo */
  getAdjustmentsByYear: async (
    year: number,
  ): Promise<ClientAdjustmentHistoryDto[]> => {
    const { data } = await apiClient.get<GetClientAdjustmentsByYearResponse | ClientAdjustmentHistoryDto[]>(
      `/clients/adjustments/year/${year}`,
    );
    const rawList: any[] = Array.isArray(data)
      ? data
      : Array.isArray((data as any)?.data)
        ? (data as any).data
        : [];
    return rawList.map((item) => ({
      id: item.id,
      clientId: item.clientId ?? item.client_id,
      cycleYear: item.cycleYear ?? item.year ?? year,
      adjustment: item.adjustment ?? 0,
      comment: item.comment ?? "",
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    }));
  },

  /** POST /clients/adjustments/bulk — carga em lote (upsert) */
  bulkUpdate: (payload: BulkClientAdjustmentDto) =>
    apiClientIdToken.post<{ message: string; count: number }>(
      "/clients/adjustments/bulk",
      payload,
    ),
};

/* Commercial Cycles API (admin — gestão de ciclos comerciais) */
export interface CommercialCycle {
  cycleId: number;
  year: number;
  status: "Draft" | "Published" | "Open" | "Closing" | "Closed";
  openedAt?: string;
  publishedAt?: string;
  closingAt?: string;
  closedAt?: string;
}

export interface CyclePreview {
  totalProfileClients: number;
  totalClients: number;
  totalProducts: number;
  alreadyGenerated: number;
  /**
   * Cada item é uma mensagem descritiva, ex.:
   * "252 cliente(s) sem reajuste cadastrado ... para o ciclo 2028 (...): 10, 67, 72, ..."
   * — ver `parseCyclePreviewConflict` em GenerateDraftDialog.tsx para extrair a lista de IDs.
   */
  conflicts: string[];
}

export interface GenerateDraftRunTrigger {
  runId: number;
  sourceYear: number;
  targetYear: number;
  status: "Running";
}

export interface GenerateDraftRun {
  runId: number;
  sourceYear: number;
  targetYear: number;
  status: "Running" | "Completed" | "Failed" | "Scheduled" | "Triggered" | "Cancelled";
  startedAt: string | null;
  finishedAt: string | null;
  generatedCount: number;
  skippedCount: number;
  clientsWithoutAdjustment: number[];
  errorMessage: string | null;
  /** Para uma linha `Scheduled`, é quem criou o agendamento (não quem disparou). */
  triggeredBy: string;
  /** Data/hora agendada (UTC), preenchida quando `status` é/foi `Scheduled`. */
  scheduledAt: string | null;
  /** Id do run real (Running/Completed/Failed) criado quando o agendamento dispara. */
  triggeredRunId: number | null;
  /** Quem cancelou, preenchido quando `status` vira `Cancelled`. */
  cancelledBy: string | null;
}

/** DTO devolvido por GET /admin/cycles/generate-draft/runs/{runId}/progress */
export interface CycleRunProgressDto {
  runId: number;
  status: "Running" | "Completed" | "Failed";
  totalExpected: number;
  processed: number;
  percentComplete: number;
}

export const commercialCyclesApi = {
  scheduleRollover: (sourceYear: number, targetYear: number, scheduledAtUtc: string) =>
    apiClientIdToken.post<GenerateDraftRun>("/admin/cycles/rollover-schedules", {
      sourceYear,
      targetYear,
      scheduledAtUtc,
    }),
  cancelRolloverSchedule: (runId: number) =>
    apiClientIdToken.post<GenerateDraftRun>(`/admin/cycles/rollover-schedules/${runId}/cancel`, {}),
  listRolloverSchedules: () =>
    apiClientIdToken.get<{ data: GenerateDraftRun[] }>("/admin/cycles/rollover-schedules"),
  list: () => apiClientIdToken.get<{ data: CommercialCycle[] }>("/admin/cycles"),
  preview: (sourceYear: number, targetYear: number) =>
    apiClientIdToken.get<CyclePreview>(
      `/admin/cycles/preview?source=${sourceYear}&target=${targetYear}`,
    ),
  generateDraft: (sourceYear: number, targetYear: number) =>
    apiClientIdToken.post<GenerateDraftRunTrigger>("/admin/cycles/generate-draft", {
      sourceYear,
      targetYear,
    }),
  getGenerateDraftRun: (runId: number) =>
    apiClientIdToken.get<GenerateDraftRun>(`/admin/cycles/generate-draft/runs/${runId}`),
  getGenerateDraftRunProgress: (runId: number) =>
    apiClientIdToken.get<CycleRunProgressDto>(`/admin/cycles/generate-draft/runs/${runId}/progress`),
  listGenerateDraftRuns: (sourceYear: number, targetYear: number) =>
    apiClientIdToken.get<{ data: GenerateDraftRun[] }>(
      `/admin/cycles/generate-draft/runs?sourceYear=${sourceYear}&targetYear=${targetYear}`,
    ),
  publish: (year: number) =>
    apiClientIdToken.post<CommercialCycle>(`/admin/cycles/${year}/publish`, {}),
  open: (year: number) =>
    apiClientIdToken.post<CommercialCycle>(`/admin/cycles/${year}/open`, {}),
  startClosing: (year: number) =>
    apiClientIdToken.post<CommercialCycle>(`/admin/cycles/${year}/start-closing`, {}),
  close: (year: number) =>
    apiClientIdToken.post<CommercialCycle>(`/admin/cycles/${year}/close`, {}),
};

export const commercialRevenueApi = {
  get: (query: CommercialRevenueQuery): Promise<CommercialRevenuePage> =>
    apiClientIdToken.get<CommercialRevenuePage>("/commercial-revenue", {
      params: {
        cycleYear: query.cycleYear,
        pageNumber: query.pageNumber ?? 1,
        pageSize: query.pageSize ?? 25,
        sortBy: query.sortBy ?? "forecastAmount",
        sortDirection: query.sortDirection ?? "desc",
        search: query.search?.trim() || undefined,
      },
    }),
  /** Exporta todos os clientes do ciclo em .xlsx, com a mesma busca e ordenação da tela (sem paginação). */
  exportXlsx: (
    query: Omit<CommercialRevenueQuery, "pageNumber" | "pageSize">,
  ): Promise<Blob> =>
    apiClientIdToken.get<Blob>("/commercial-revenue", {
      params: {
        export: true,
        cycleYear: query.cycleYear,
        sortBy: query.sortBy ?? "forecastAmount",
        sortDirection: query.sortDirection ?? "desc",
        search: query.search?.trim() || undefined,
      },
      responseType: "blob",
    }),
  getClientDetails: (
    clientId: number,
    cycleYear: number,
  ): Promise<CommercialRevenueClientDetails> =>
    apiClientIdToken.get<CommercialRevenueClientDetails>(
      `/commercial-revenue/${clientId}`,
      { params: { cycleYear } },
    ),
  /** Exporta os pedidos e itens do cliente no ciclo em .xlsx. */
  exportClientDetailsXlsx: (clientId: number, cycleYear: number): Promise<Blob> =>
    apiClientIdToken.get<Blob>(`/commercial-revenue/${clientId}`, {
      params: { cycleYear, export: true },
      responseType: "blob",
    }),
};

/** Monta a query string dos filtros do relatório de itens, compartilhada pela consulta e pela exportação. */
const buildOrderItemsReportParams = (
  filter: import("@/types/reports").OrderItemsReportFilter
) => {
  const params = new URLSearchParams();
  params.append("startDate", filter.startDate);
  params.append("endDate", filter.endDate);
  if (filter.clientCnpj) params.append("clientCnpj", filter.clientCnpj);
  if (filter.cycleCode) params.append("cycleCode", filter.cycleCode.toString());
  if (filter.cycleYear) params.append("cycleYear", filter.cycleYear.toString());
  if (filter.cycleType) params.append("cycleType", filter.cycleType);
  if (filter.shippingSituation) params.append("shippingSituation", filter.shippingSituation);
  if (filter.orderStatus) params.append("orderStatus", filter.orderStatus);
  if (filter.paymentMethodCode) params.append("paymentMethodCode", filter.paymentMethodCode);
  if (filter.cardBrandCode) params.append("cardBrandCode", filter.cardBrandCode);
  if (filter.paymentCondition) params.append("paymentCondition", filter.paymentCondition);
  return params;
};

export const reportsApi = {
  /** Vocabulários, catálogos de pagamento e limites: a tela monta os filtros a partir daqui. */
  getFilterOptions: () =>
    apiClientIdToken.get<import("@/types/reports").OrderItemsReportFilterOptions>(
      "/orders/reports/items/filter-options"
    ),

  getOrderItems: (filter: import("@/types/reports").OrderItemsReportFilter) => {
    const params = buildOrderItemsReportParams(filter);
    if (filter.pageNumber) params.append("pageNumber", filter.pageNumber.toString());
    if (filter.pageSize) params.append("pageSize", filter.pageSize.toString());

    return apiClientIdToken.get<import("@/types/reports").OrderItemsReportPage>(`/orders/reports/items?${params.toString()}`);
  },

  startExportJob: (
    filter: Omit<import("@/types/reports").OrderItemsReportFilter, "pageNumber" | "pageSize">
  ) => {
    return apiClientIdToken.post<import("@/types/reports").ReportExportJobResponse>(
      "/orders/reports/items/export-job",
      filter
    );
  },

  getExportJobStatus: (jobId: string) => {
    return apiClientIdToken.get<import("@/types/reports").ReportExportJobResponse>(
      `/orders/reports/items/export-job/${jobId}`
    );
  },
};

/* Legacy compatibility API object (to maintain backward compatibility) */
export const api = {
  productsget: async (endpoint: string) => {
    if (endpoint === "/products") return await productsApi.getAll();

    throw new Error(`Endpoint ${endpoint} not implemented`);
  },
};

export default apiClient;
